import { z } from 'zod'

import { ModuleTypeEnum } from './module-type'
import { decimalSchema } from './prisma-zod'
import { ResolvedFromValues } from './rate-snapshot'
import { TimeEntryStatusTierSchema } from './time-entry-status-tier'

// =====================
// Employment Types
// =====================

export enum EmploymentType {
  FULL_TIME = 'FULL_TIME',
  PART_TIME = 'PART_TIME',
  CONTRACTOR = 'CONTRACTOR',
  FREELANCER = 'FREELANCER',
}

/** One spelling everywhere an employment type is shown ("Full-time"). */
export const EMPLOYMENT_TYPE_LABELS: Record<EmploymentType, string> = {
  [EmploymentType.FULL_TIME]: 'Full-time',
  [EmploymentType.PART_TIME]: 'Part-time',
  [EmploymentType.CONTRACTOR]: 'Contractor',
  [EmploymentType.FREELANCER]: 'Freelancer',
}

/**
 * Contractor-style members are paid through rates rather than a compensation
 * package. Deny-list semantics: a missing or unknown employment type counts as
 * W2, matching the schema default of FULL_TIME.
 */
export function isContractorEmploymentType(
  type: string | null | undefined,
): boolean {
  return (
    type === EmploymentType.CONTRACTOR || type === EmploymentType.FREELANCER
  )
}

export function isW2EmploymentType(type: string | null | undefined): boolean {
  return !isContractorEmploymentType(type)
}

/**
 * Plan 4 allocation lifecycle states.
 *
 * Legacy vocabulary (ACTIVE / PLANNED / COMPLETED / CANCELLED) was replaced
 * by an explicit commitment-vs-plan distinction:
 *   ACTIVE     -> CONFIRMED
 *   PLANNED    -> TENTATIVE
 *   COMPLETED  -> ARCHIVED
 *   CANCELLED  -> ARCHIVED
 *   member_id IS NULL -> PLACEHOLDER
 */
export enum AllocationStatus {
  TENTATIVE = 'TENTATIVE',
  CONFIRMED = 'CONFIRMED',
  PLACEHOLDER = 'PLACEHOLDER',
  ARCHIVED = 'ARCHIVED',
}

/**
 * Plan 4 / Plan 5 shared allocation state machine.
 *
 * Single source of truth for the set of statuses reachable from any given
 * status. Consumed by:
 *   - apps/api/.../allocation-transition.service.ts (backend validation)
 *   - apps/web/.../AllocationStatusSelector.tsx (status dropdown options)
 *   - apps/web/.../AllocationFormDialog.tsx (form status list)
 *   - apps/web/.../planner (Plan 5 Resource Planner)
 *
 * Rules (§5.1 of allocation-states-data-model-and-ui-behavior.md):
 *   PLACEHOLDER -> TENTATIVE | CONFIRMED | ARCHIVED
 *   TENTATIVE   -> CONFIRMED | ARCHIVED
 *   CONFIRMED   -> TENTATIVE | ARCHIVED
 *   ARCHIVED    -> ✕
 */
export const ALLOCATION_STATE_MACHINE: Record<
  AllocationStatus,
  AllocationStatus[]
> = {
  [AllocationStatus.PLACEHOLDER]: [
    AllocationStatus.TENTATIVE,
    AllocationStatus.CONFIRMED,
    AllocationStatus.ARCHIVED,
  ],
  [AllocationStatus.TENTATIVE]: [
    AllocationStatus.CONFIRMED,
    AllocationStatus.ARCHIVED,
  ],
  [AllocationStatus.CONFIRMED]: [
    AllocationStatus.TENTATIVE,
    AllocationStatus.ARCHIVED,
  ],
  [AllocationStatus.ARCHIVED]: [],
}

/**
 * Statuses permitted when creating a new allocation (Plan 4 §6.3).
 * ARCHIVED is never a creation status — archive is a terminal transition only.
 */
export const ALLOCATION_CREATION_STATUSES: AllocationStatus[] = [
  AllocationStatus.TENTATIVE,
  AllocationStatus.CONFIRMED,
  AllocationStatus.PLACEHOLDER,
]

/**
 * Statuses permitted when fulfilling a demand (Plan 6c scope-change 6.D).
 * PLACEHOLDER is excluded — fulfillment requires a named person.
 * ARCHIVED is excluded — it is a terminal state, not a creation target.
 */
export const FULFILLMENT_TARGET_STATUSES: AllocationStatus[] = [
  AllocationStatus.TENTATIVE,
  AllocationStatus.CONFIRMED,
]

/**
 * Plan 4 allocation history action vocabulary.
 */
export enum AllocationHistoryAction {
  CREATED = 'CREATED',
  STATUS_CHANGED = 'STATUS_CHANGED',
  DATES_CHANGED = 'DATES_CHANGED',
  HOURS_CHANGED = 'HOURS_CHANGED',
  ROLE_CHANGED = 'ROLE_CHANGED',
  MEMBER_ASSIGNED = 'MEMBER_ASSIGNED',
  MEMBER_CHANGED = 'MEMBER_CHANGED',
  ARCHIVED = 'ARCHIVED',
  NOTES_UPDATED = 'NOTES_UPDATED',
}

export enum AllocationType {
  PROJECT = 'PROJECT',
  CLIENT = 'CLIENT',
  DEAL = 'DEAL',
  INTERNAL = 'INTERNAL',
  PTO = 'PTO',
}

export enum RateType {
  FIXED = 'FIXED',
  COST_PLUS = 'COST_PLUS',
  AT_COST = 'AT_COST',
}

/**
 * How a COST_PLUS markup was entered and should be displayed. The stored
 * value is always `markupPercent`; MULTIPLIER only changes notation (3x = 200%).
 */
export enum MarkupBasis {
  PERCENT = 'PERCENT',
  MULTIPLIER = 'MULTIPLIER',
}

export enum RateSource {
  MANUAL = 'MANUAL',
  COMPENSATION = 'COMPENSATION',
}

export enum PricingModel {
  TIME_AND_MATERIALS = 'TIME_AND_MATERIALS',
  FIXED_FEE = 'FIXED_FEE',
  BLENDED = 'BLENDED',
}

export enum EarningsStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  PROCESSING = 'PROCESSING',
  PAID = 'PAID',
  DISPUTED = 'DISPUTED',
}

export enum PeriodType {
  DAY = 'DAY',
  WEEK = 'WEEK',
  MONTH = 'MONTH',
}

export enum UtilizationCalculationTrigger {
  /** Nightly / weekly sweep recalculation. */
  SCHEDULED = 'SCHEDULED',
  /** Debounced event-driven recalculation (time entry approval, submission, allocation change). */
  EVENT = 'EVENT',
  /** User clicked Refresh on a utilization surface. Reserve user-facing
   *  "refreshed by" / "last refreshed" copy for this value only. */
  MANUAL = 'MANUAL',
  /** Row written by an admin-run Plan 1 foundation rebuild
   *  (`POST /member-resources/plan-1/rebuild-foundation`). Non-user-attributable;
   *  contributes to staleness through `calculatedAt` alone. */
  REBUILD = 'REBUILD',
  /** Row populated by a migration (e.g. the freshness-columns backfill or its
   *  follow-up retagging migration). Non-user-attributable; contributes to
   *  staleness through `calculatedAt` alone. */
  BACKFILL = 'BACKFILL',
}

/**
 * Plan 9a T41a: maps a raw `UtilizationCalculationTrigger` to the three
 * user-facing copy kinds defined in Implementation Addendum §B.
 *
 * - MANUAL  → user clicked Refresh; "Refreshed by <user>" copy is appropriate.
 * - AUTO    → system-driven (SCHEDULED sweep or EVENT debounce); "Last updated" copy.
 * - SYSTEM  → non-user-attributable bulk operation (REBUILD, BACKFILL, or unknown);
 *             show timestamp only, no actor attribution.
 */
export function deriveFreshnessCopyKind(
  trigger: UtilizationCalculationTrigger | null | undefined,
): 'MANUAL' | 'AUTO' | 'SYSTEM' {
  if (trigger === UtilizationCalculationTrigger.MANUAL) return 'MANUAL'
  if (
    trigger === UtilizationCalculationTrigger.SCHEDULED ||
    trigger === UtilizationCalculationTrigger.EVENT
  ) {
    return 'AUTO'
  }
  return 'SYSTEM' // REBUILD, BACKFILL, or null/undefined
}

// Plan 9a: multi-metric utilization selector
export enum UtilizationMetric {
  BILLABLE = 'BILLABLE',
  TOTAL = 'TOTAL',
  PRODUCTIVE = 'PRODUCTIVE',
  TARGET_VARIANCE = 'TARGET_VARIANCE',
  REVENUE_ATTAINMENT = 'REVENUE_ATTAINMENT',
}

// Plan 9a: Revenue/Profit card tier selection for Team Insights and Workload cards.
// FINANCIAL_ACTUALS is the Plan 9 default; OPERATIONAL_VISIBILITY is the escape
// hatch for organizations that want the OV presentation on the money cards.
export enum RevenueTier {
  FINANCIAL_ACTUALS = 'FINANCIAL_ACTUALS',
  OPERATIONAL_VISIBILITY = 'OPERATIONAL_VISIBILITY',
}

// =====================
// Member Rate (Rate Card)
// =====================

export const MemberRateSchema = z.object({
  id: z.string().uuid(),
  memberId: z.string().uuid(),
  organizationId: z.string().uuid(),
  rateType: z.nativeEnum(RateType).default(RateType.FIXED),
  markupPercent: decimalSchema.optional().nullable(), // For COST_PLUS: e.g., 25.00 = 25% (canonical even when entered as a multiplier)
  markupBasis: z.nativeEnum(MarkupBasis).default(MarkupBasis.PERCENT), // Display notation only
  payRate: decimalSchema,
  billRate: decimalSchema.optional().nullable(),
  costRate: decimalSchema.optional().nullable(),
  currency: z.string().default('USD'),
  entityId: z.string().uuid().optional().nullable(),
  projectId: z.string().uuid().optional().nullable(),
  taskId: z.string().uuid().optional().nullable(),
  roleId: z.string().uuid().optional().nullable(),
  effectiveFrom: z.date(),
  effectiveTo: z.date().optional().nullable(),
  isActive: z.boolean().default(true),
  notes: z.string().optional().nullable(),
  rateSource: z.nativeEnum(RateSource).default(RateSource.MANUAL),
  sourceCompensationId: z.string().uuid().optional().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  member: z.any().optional(),
  entity: z.any().optional(),
  project: z.any().optional(),
  task: z.any().optional(),
  role: z.any().optional(),
})

// Context parameter for getEffectiveRate() — describes the billing context
export const EffectiveRateContextSchema = z.object({
  taskId: z.string().uuid().optional(),
  projectId: z.string().uuid().optional(),
  entityId: z.string().uuid().optional(),
  roleId: z.string().uuid().optional(), // Phase 2: explicit role for rate card matching
  date: z.date().optional(),
})

export type EffectiveRateContext = z.infer<typeof EffectiveRateContextSchema>

// Extended return type for getEffectiveRate() with resolution metadata
export const EffectiveRateResultSchema = MemberRateSchema.extend({
  resolvedFrom: z.enum(ResolvedFromValues).optional(),
  appliedDiscount: decimalSchema.optional().nullable(), // Total discount % applied
  discountSource: z.enum(['PROJECT', 'CLIENT']).optional().nullable(), // Source of the applied discount
  isBlended: z.boolean().optional(), // Whether this is a blended project rate
  billCurrency: z.string().nullable().optional(), // Explicit project-rate currency when blended
  payCurrency: z.string().nullable().optional(), // Currency of the resolved pay source
  costCurrency: z.string().nullable().optional(), // Currency of the resolved cost source
  effectiveBillRate: decimalSchema.optional().nullable(), // Final bill rate after discount/markup
  resolvedRoleId: z.string().uuid().optional().nullable(), // Phase 2: roleId used for resolution (audit trail)
  payCostSource: z.enum(['COMPENSATION', 'MEMBER_RATE', 'NONE']).optional(), // Phase 2: discriminator for null pay/cost tooltips
})

export type EffectiveRateResult = z.infer<typeof EffectiveRateResultSchema>

// RateChangeContext — determines which time entries are affected by a rate change.
// Used by propagateRateChanges() to scope the re-resolution query.
export interface RateChangeContext {
  triggeredBy:
    | 'MEMBER_RATE_CHANGE'
    | 'RATE_CARD_ENTRY_CHANGE'
    | 'RATE_CARD_ASSIGNMENT'
    | 'RATE_CARD_REMOVAL'
    | 'COMPENSATION_CHANGE'
    | 'PROJECT_PRICING_CHANGE'
    | 'ENTITY_DISCOUNT_CHANGE'
    | 'ORG_DEFAULT_CHANGE'
    | 'MEMBER_ROLE_CHANGE'

  organizationId: string

  memberId?: string
  rateCardId?: string
  projectId?: string
  entityId?: string
  roleId?: string

  // When provided, only entries with these statuses are re-resolved.
  // Defaults to all unapproved statuses: DRAFT, SUBMITTED, CHANGES_REQUESTED, REJECTED.
  statusFilter?: ('DRAFT' | 'SUBMITTED' | 'CHANGES_REQUESTED' | 'REJECTED')[]

  initiatedBy: string
  initiatedAt: Date
}

// Returned by checkRateChangeImpact — counts of affected entries by status.
export interface RateChangeImpact {
  draftCount: number
  submittedCount: number
}

export type MemberRate = z.infer<typeof MemberRateSchema>

export const CreateMemberRateSchema = MemberRateSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  member: true,
  entity: true,
  project: true,
  task: true,
})

export type CreateMemberRate = z.infer<typeof CreateMemberRateSchema>

export const UpdateMemberRateSchema = CreateMemberRateSchema.partial().omit({
  memberId: true,
  organizationId: true,
})

export type UpdateMemberRate = z.infer<typeof UpdateMemberRateSchema>

// =====================
// Job Role (Organization Role Lookup)
// =====================

export const JobRoleSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  name: z.string().max(100),
  description: z.string().optional().nullable(),
  isActive: z.boolean().default(true),
  createdAt: z.date(),
  updatedAt: z.date(),
})

export type JobRole = z.infer<typeof JobRoleSchema>

export const CreateJobRoleSchema = JobRoleSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
})

export type CreateJobRole = z.infer<typeof CreateJobRoleSchema>

export const UpdateJobRoleSchema = CreateJobRoleSchema.partial().omit({
  organizationId: true,
})

export type UpdateJobRole = z.infer<typeof UpdateJobRoleSchema>

// =====================
// Multi-Role Assignment (Phase 1)
// =====================

export const MemberOrganizationRoleSchema = z.object({
  id: z.string().uuid(),
  memberId: z.string().uuid(),
  organizationId: z.string().uuid(),
  roleId: z.string().uuid(),
  isPrimary: z.boolean().default(false),
  // T15 split-role capacity: the share of the member's single weekly capacity
  // bucket this role gets. Stored as a 0..1 fraction; relative weights across a
  // member's roles, normalized at read time. Decimal serializes to string over
  // JSON, so coerce to a number.
  capacityFraction: z.coerce.number().default(1),
  createdAt: z.date(),
  updatedAt: z.date(),
  role: JobRoleSchema.optional(),
})

export type MemberOrganizationRole = z.infer<
  typeof MemberOrganizationRoleSchema
>

export const AssignMemberRoleSchema = z.object({
  roleId: z.string().uuid(),
  isPrimary: z.boolean().optional().default(false),
})

export type AssignMemberRole = z.infer<typeof AssignMemberRoleSchema>

// T15 (U4): update a member-role's capacity share. The fraction is a 0..1
// share of the member's weekly capacity; values across a member's roles are
// treated as relative weights and normalized at read time.
export const SetMemberRoleCapacitySchema = z.object({
  capacityFraction: z.number().min(0).max(1),
})

export type SetMemberRoleCapacity = z.infer<typeof SetMemberRoleCapacitySchema>

export const EntityJobRoleSelectionSchema = z.object({
  id: z.string().uuid(),
  entityId: z.string().uuid(),
  organizationId: z.string().uuid(),
  roleId: z.string().uuid(),
  createdAt: z.date(),
  updatedAt: z.date(),
  role: JobRoleSchema.optional(),
})

export type EntityJobRoleSelection = z.infer<
  typeof EntityJobRoleSelectionSchema
>

export const SetEntityJobRoleSelectionsSchema = z.object({
  roleIds: z.array(z.string().uuid()).max(100),
  confirmed: z.boolean().optional(),
})

export type SetEntityJobRoleSelections = z.infer<
  typeof SetEntityJobRoleSelectionsSchema
>

export const ProjectMemberJobRoleSchema = z.object({
  id: z.string().uuid(),
  projectMemberId: z.string().uuid(),
  organizationId: z.string().uuid(),
  roleId: z.string().uuid(),
  createdAt: z.date(),
  updatedAt: z.date(),
  role: JobRoleSchema.optional(),
})

export type ProjectMemberJobRole = z.infer<typeof ProjectMemberJobRoleSchema>

export const SetProjectMemberJobRolesSchema = z.object({
  roleIds: z.array(z.string().uuid()).max(100),
})

export type SetProjectMemberJobRoles = z.infer<
  typeof SetProjectMemberJobRolesSchema
>

// Per-member client-level billing assignment.
// Default billing roster for all projects under an entity; drives client-direct
// time entries as well.
export const EntityMemberJobRoleSchema = z.object({
  id: z.string().uuid(),
  entityId: z.string().uuid(),
  memberId: z.string().uuid(),
  organizationId: z.string().uuid(),
  roleId: z.string().uuid(),
  createdAt: z.date(),
  updatedAt: z.date(),
  role: JobRoleSchema.optional(),
})

export type EntityMemberJobRole = z.infer<typeof EntityMemberJobRoleSchema>

// Set the full billing role set for a single member on one entity.
// Replaces all current EntityMemberJobRole rows for that (entity, member).
export const SetEntityMemberJobRolesSchema = z.object({
  roleIds: z.array(z.string().uuid()).max(100),
  /**
   * When true, the server will apply the change even if it would impact
   * projects currently inheriting from the entity. When absent/false, the
   * server returns a 409-shaped preview describing the impact for the client
   * to confirm.
   */
  confirmed: z.boolean().optional(),
})

export type SetEntityMemberJobRoles = z.infer<
  typeof SetEntityMemberJobRolesSchema
>

// =====================
// Rate Card
// =====================

export const RateCardEntrySchema = z.object({
  id: z.string().uuid(),
  rateCardId: z.string().uuid(),
  roleId: z.string().uuid(),
  billRate: decimalSchema,
  createdAt: z.date(),
  updatedAt: z.date(),
  role: z.any().optional(),
})

export type RateCardEntry = z.infer<typeof RateCardEntrySchema>

export const RateCardSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  name: z.string().max(100),
  description: z.string().optional().nullable(),
  currency: z.string().default('USD'),
  isActive: z.boolean().default(true),
  effectiveFrom: z.date(),
  effectiveTo: z.date().optional().nullable(),
  createdBy: z.string().uuid().optional().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  entries: z.array(RateCardEntrySchema).optional(),
  creator: z.any().optional(),
})

export type RateCard = z.infer<typeof RateCardSchema>

export const CreateRateCardSchema = RateCardSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  creator: true,
})

export type CreateRateCard = z.infer<typeof CreateRateCardSchema>

export const UpdateRateCardSchema = CreateRateCardSchema.partial().omit({
  organizationId: true,
})

export type UpdateRateCard = z.infer<typeof UpdateRateCardSchema>

export const CreateRateCardEntrySchema = RateCardEntrySchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  role: true,
})

export type CreateRateCardEntry = z.infer<typeof CreateRateCardEntrySchema>

// =====================
// Budget Types
// =====================

export enum BudgetRuleType {
  MAX_HOURS_PER_RESOURCE = 'MAX_HOURS_PER_RESOURCE',
  MARGIN_THRESHOLD = 'MARGIN_THRESHOLD',
  CATEGORY_LIMIT = 'CATEGORY_LIMIT',
  APPROVAL_REQUIRED = 'APPROVAL_REQUIRED',
  DATE_CONSTRAINT = 'DATE_CONSTRAINT',
  DISCOUNT_LIMIT = 'DISCOUNT_LIMIT',
}

export enum BudgetRuleAction {
  WARN = 'WARN',
  BLOCK = 'BLOCK',
}

export enum BudgetExceptionStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

// Budget Category
export const BudgetCategorySchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  name: z.string().max(100),
  description: z.string().optional().nullable(),
  isDefault: z.boolean().default(false),
  displayOrder: z.number().int().default(0),
  isActive: z.boolean().default(true),
  createdAt: z.date(),
  updatedAt: z.date(),
})

export type BudgetCategory = z.infer<typeof BudgetCategorySchema>

export const CreateBudgetCategorySchema = BudgetCategorySchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
})
export type CreateBudgetCategory = z.infer<typeof CreateBudgetCategorySchema>

export const UpdateBudgetCategorySchema =
  CreateBudgetCategorySchema.partial().omit({
    organizationId: true,
  })
export type UpdateBudgetCategory = z.infer<typeof UpdateBudgetCategorySchema>

// Budget Template
export const BudgetTemplateCategorySchema = z.object({
  id: z.string().uuid(),
  templateId: z.string().uuid(),
  categoryId: z.string().uuid(),
  defaultPercent: decimalSchema.optional().nullable(),
  defaultHours: decimalSchema.optional().nullable(),
  displayOrder: z.number().int().default(0),
  createdAt: z.date(),
  updatedAt: z.date(),
  category: z.any().optional(),
})

export type BudgetTemplateCategory = z.infer<
  typeof BudgetTemplateCategorySchema
>

export const BudgetRuleSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  templateId: z.string().uuid().optional().nullable(),
  ruleType: z.nativeEnum(BudgetRuleType),
  action: z.nativeEnum(BudgetRuleAction).default(BudgetRuleAction.WARN),
  threshold: decimalSchema.optional().nullable(),
  operator: z.string().default('>='),
  message: z.string().optional().nullable(),
  appliesToCategoryId: z.string().uuid().optional().nullable(),
  appliesToRoleId: z.string().uuid().optional().nullable(),
  isActive: z.boolean().default(true),
  createdAt: z.date(),
  updatedAt: z.date(),
})

export type BudgetRule = z.infer<typeof BudgetRuleSchema>

export const BudgetTemplateSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  name: z.string().max(100),
  description: z.string().optional().nullable(),
  isDefault: z.boolean().default(false),
  isActive: z.boolean().default(true),
  createdBy: z.string().uuid().optional().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  categories: z.array(BudgetTemplateCategorySchema).optional(),
  rules: z.array(BudgetRuleSchema).optional(),
})

export type BudgetTemplate = z.infer<typeof BudgetTemplateSchema>

export const CreateBudgetTemplateSchema = BudgetTemplateSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  categories: true,
  rules: true,
})
export type CreateBudgetTemplate = z.infer<typeof CreateBudgetTemplateSchema>

export const UpdateBudgetTemplateSchema =
  CreateBudgetTemplateSchema.partial().omit({
    organizationId: true,
  })
export type UpdateBudgetTemplate = z.infer<typeof UpdateBudgetTemplateSchema>

// Project Budget
export const ProjectBudgetCategorySchema = z.object({
  id: z.string().uuid(),
  projectBudgetId: z.string().uuid(),
  budgetCategoryId: z.string().uuid(),
  allocatedHours: decimalSchema.optional().nullable(),
  allocatedCost: decimalSchema.optional().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  budgetCategory: z.any().optional(),
})

export type ProjectBudgetCategory = z.infer<typeof ProjectBudgetCategorySchema>

export const ProjectBudgetSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  templateId: z.string().uuid().optional().nullable(),
  totalBudgetHours: decimalSchema.optional().nullable(),
  totalBudgetCost: decimalSchema.optional().nullable(),
  totalBudgetRevenue: decimalSchema.optional().nullable(),
  marginTargetPercent: decimalSchema.optional().nullable(),
  createdBy: z.string().uuid().optional().nullable(),
  createdAt: z.date(),
  categories: z.array(ProjectBudgetCategorySchema).optional(),
})

export type ProjectBudget = z.infer<typeof ProjectBudgetSchema>

export const CreateProjectBudgetSchema = ProjectBudgetSchema.omit({
  id: true,
  createdAt: true,
  categories: true,
})
export type CreateProjectBudget = z.infer<typeof CreateProjectBudgetSchema>

// Project Phase
export const ProjectPhaseSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  name: z.string().max(255),
  description: z.string().optional().nullable(),
  startDate: z.date().optional().nullable(),
  endDate: z.date().optional().nullable(),
  status: z.string().default('PLANNED'),
  sortOrder: z.number().int().default(0),
  createdAt: z.date(),
  updatedAt: z.date(),
})

export type ProjectPhase = z.infer<typeof ProjectPhaseSchema>

export const CreateProjectPhaseSchema = ProjectPhaseSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
})
export type CreateProjectPhase = z.infer<typeof CreateProjectPhaseSchema>

// Budget Exception Request
export const BudgetExceptionRequestSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  organizationId: z.string().uuid(),
  exceptionType: z.string(), // HOURS, COST, MARGIN
  ruleId: z.string().uuid().optional().nullable(),
  currentValue: decimalSchema,
  requestedValue: decimalSchema,
  reason: z.string(),
  status: z
    .nativeEnum(BudgetExceptionStatus)
    .default(BudgetExceptionStatus.PENDING),
  reviewedBy: z.string().uuid().optional().nullable(),
  reviewedAt: z.date().optional().nullable(),
  rejectionReason: z.string().optional().nullable(),
  createdBy: z.string().uuid(),
  createdAt: z.date(),
  updatedAt: z.date(),
  project: z.any().optional(),
  reviewer: z.any().optional(),
  creator: z.any().optional(),
})

export type BudgetExceptionRequest = z.infer<
  typeof BudgetExceptionRequestSchema
>

export const CreateBudgetExceptionRequestSchema =
  BudgetExceptionRequestSchema.omit({
    id: true,
    status: true,
    reviewedBy: true,
    reviewedAt: true,
    rejectionReason: true,
    createdAt: true,
    updatedAt: true,
    project: true,
    reviewer: true,
    creator: true,
  })
export type CreateBudgetExceptionRequest = z.infer<
  typeof CreateBudgetExceptionRequestSchema
>

// Budget validation result (returned by validateAllocations API)
export const BudgetViolationSchema = z.object({
  ruleId: z.string().uuid().optional(),
  ruleType: z.nativeEnum(BudgetRuleType),
  action: z.nativeEnum(BudgetRuleAction),
  message: z.string(),
  currentValue: z.number(),
  threshold: z.number(),
})

export type BudgetViolation = z.infer<typeof BudgetViolationSchema>

export const BudgetValidationResultSchema = z.object({
  valid: z.boolean(),
  violations: z.array(BudgetViolationSchema),
  warnings: z.array(BudgetViolationSchema), // WARN actions
  blocks: z.array(BudgetViolationSchema), // BLOCK actions
})

export type BudgetValidationResult = z.infer<
  typeof BudgetValidationResultSchema
>

// =====================
// Project Forecast
// =====================

export const ProjectForecastSnapshotSchema = z.object({
  currency: z.string().optional().nullable(),
  unavailableReason: z.string().optional().nullable(),
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  snapshotDate: z.date(),
  snapshotType: z.string().default('AUTO'),
  triggerEvent: z.string().optional().nullable(),
  forecastCost: decimalSchema.optional().nullable(),
  forecastRevenue: decimalSchema.optional().nullable(),
  forecastMargin: decimalSchema.optional().nullable(),
  totalPlannedHours: decimalSchema.optional().nullable(),
  totalActualHours: decimalSchema.optional().nullable(),
  data: z.any().optional().nullable(),
  createdBy: z.string().uuid().optional().nullable(),
  createdAt: z.date(),
})

export type ProjectForecastSnapshot = z.infer<
  typeof ProjectForecastSnapshotSchema
>

// Availability check result
export const MemberAvailabilityCheckSchema = z.object({
  available: z.boolean(),
  availableHoursPerWeek: z.number(),
  currentAllocatedHoursPerWeek: z.number(),
  maxCapacity: z.number(),
  requestedHoursPerWeek: z.number(),
  conflicts: z.array(
    z.object({
      type: z.enum(['PTO', 'HOLIDAY', 'OVER_ALLOCATED']),
      date: z.string().optional(),
      details: z.string().optional(),
    }),
  ),
})

export type MemberAvailabilityCheck = z.infer<
  typeof MemberAvailabilityCheckSchema
>

// =====================
// Allocation Constraints
// =====================

export enum AllocationConstraintType {
  MAX_HOURS_PER_MONTH = 'MAX_HOURS_PER_MONTH',
  MAX_PERCENT_PER_ROLE = 'MAX_PERCENT_PER_ROLE',
  EMPLOYMENT_TYPE_RESTRICTION = 'EMPLOYMENT_TYPE_RESTRICTION',
  REQUIRE_APPROVAL_ABOVE = 'REQUIRE_APPROVAL_ABOVE',
}

export const AllocationConstraintSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  constraintType: z.nativeEnum(AllocationConstraintType),
  threshold: decimalSchema.optional().nullable(),
  operator: z.string().default('<='),
  action: z.nativeEnum(BudgetRuleAction).default(BudgetRuleAction.WARN),
  message: z.string().optional().nullable(),
  appliesToRoleId: z.string().uuid().optional().nullable(),
  appliesToEmploymentType: z.string().optional().nullable(),
  isActive: z.boolean().default(true),
  createdAt: z.date(),
  updatedAt: z.date(),
  appliesToRole: z.any().optional(),
})

export type AllocationConstraint = z.infer<typeof AllocationConstraintSchema>

// Allocation Exception Request
export const AllocationExceptionRequestSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  memberId: z.string().uuid(),
  projectId: z.string().uuid().optional().nullable(),
  entityId: z.string().uuid().optional().nullable(),
  constraintId: z.string().uuid(),
  requestedHoursPerWeek: decimalSchema.optional().nullable(),
  requestedAllocationPercent: decimalSchema.optional().nullable(),
  requestedStartDate: z.date().optional().nullable(),
  requestedEndDate: z.date().optional().nullable(),
  reason: z.string(),
  status: z
    .nativeEnum(BudgetExceptionStatus)
    .default(BudgetExceptionStatus.PENDING),
  reviewedBy: z.string().uuid().optional().nullable(),
  reviewedAt: z.date().optional().nullable(),
  rejectionReason: z.string().optional().nullable(),
  createdBy: z.string().uuid(),
  createdAt: z.date(),
  updatedAt: z.date(),
  member: z.any().optional(),
  project: z.any().optional(),
  constraint: z.any().optional(),
  reviewer: z.any().optional(),
  creator: z.any().optional(),
})

export type AllocationExceptionRequest = z.infer<
  typeof AllocationExceptionRequestSchema
>

// =====================
// Resource Request / Staffing
// =====================

export enum ResourceRequestStatus {
  OPEN = 'OPEN',
  PARTIALLY_FILLED = 'PARTIALLY_FILLED',
  FILLED = 'FILLED',
  CANCELLED = 'CANCELLED',
}

export const ResourceRequestSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  projectId: z.string().uuid().optional().nullable(),
  dealId: z.string().uuid().optional().nullable(),
  title: z.string().max(255),
  description: z.string().optional().nullable(),
  roleId: z.string().uuid().optional().nullable(),
  requiredSkills: z.any().default([]),
  hoursPerWeek: decimalSchema,
  startDate: z.date(),
  endDate: z.date(),
  positionsNeeded: z.number().int().default(1),
  priority: z.string().default('MEDIUM'),
  status: z
    .nativeEnum(ResourceRequestStatus)
    .default(ResourceRequestStatus.OPEN),
  positionsFilled: z.number().int().default(0),
  createdBy: z.string().uuid(),
  createdAt: z.date(),
  updatedAt: z.date(),
  project: z.any().optional(),
  deal: z.any().optional(),
  role: z.any().optional(),
  creator: z.any().optional(),
  fulfillments: z.array(z.any()).optional(),
})

export type ResourceRequest = z.infer<typeof ResourceRequestSchema>

// =====================
// Demand Queue (Plan 6a)
// =====================

export enum DemandStatus {
  DRAFT = 'DRAFT',
  SUBMITTED = 'SUBMITTED',
  TRIAGED = 'TRIAGED',
  MATCHING = 'MATCHING',
  PARTIALLY_FULFILLED = 'PARTIALLY_FULFILLED',
  FULFILLED = 'FULFILLED',
  CLOSED = 'CLOSED',
  CANCELLED = 'CANCELLED',
}

export enum DemandSource {
  PROJECT = 'PROJECT',
  DEAL = 'DEAL',
  EXPANSION = 'EXPANSION',
  SUPPORT = 'SUPPORT',
  BACKFILL = 'BACKFILL',
  INTERNAL = 'INTERNAL',
  MANUAL = 'MANUAL',
}

export enum DemandPriority {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  CRITICAL = 'CRITICAL',
}

export enum DemandWorkerType {
  EMPLOYEE = 'EMPLOYEE',
  CONTRACTOR = 'CONTRACTOR',
  FREELANCER = 'FREELANCER',
  ANY = 'ANY',
}

/**
 * Demand lifecycle state machine (Plan 6a §5.2).
 *
 * Single source of truth for allowed transitions. Consumed by:
 *   - DemandService.transitionStatus (backend validation)
 *   - (Plan 6c) DemandStatusSelector (frontend)
 */
export const DEMAND_STATE_MACHINE: Record<DemandStatus, DemandStatus[]> = {
  [DemandStatus.DRAFT]: [DemandStatus.SUBMITTED, DemandStatus.CANCELLED],
  [DemandStatus.SUBMITTED]: [DemandStatus.TRIAGED, DemandStatus.CANCELLED],
  [DemandStatus.TRIAGED]: [DemandStatus.MATCHING, DemandStatus.CANCELLED],
  [DemandStatus.MATCHING]: [
    DemandStatus.PARTIALLY_FULFILLED,
    DemandStatus.FULFILLED,
    DemandStatus.TRIAGED,
    DemandStatus.CANCELLED,
  ],
  [DemandStatus.PARTIALLY_FULFILLED]: [
    DemandStatus.FULFILLED,
    DemandStatus.MATCHING,
    DemandStatus.CANCELLED,
  ],
  [DemandStatus.FULFILLED]: [
    DemandStatus.CLOSED,
    DemandStatus.CANCELLED,
    DemandStatus.PARTIALLY_FULFILLED,
  ],
  [DemandStatus.CLOSED]: [],
  [DemandStatus.CANCELLED]: [],
}

export function canDemandTransition(
  from: DemandStatus,
  to: DemandStatus,
): boolean {
  return DEMAND_STATE_MACHINE[from]?.includes(to) ?? false
}

/**
 * "Open" demand = still needs staffing: not in a terminal status and not fully
 * fulfilled. One rule for the demand sidebar, the Staffing tabs' request KPI
 * and card, and the project overview alert. Keep the list no wider than the
 * Prisma DemandStatus enum.
 */
export const OPEN_DEMAND_TERMINAL_STATUSES = [
  DemandStatus.FULFILLED,
  DemandStatus.CLOSED,
  DemandStatus.CANCELLED,
] as const

export const isOpenDemand = (demand: {
  status: string
  fulfillmentPercentage?: unknown
}) =>
  !(OPEN_DEMAND_TERMINAL_STATUSES as readonly string[]).includes(
    demand.status,
  ) && Number(demand.fulfillmentPercentage ?? 0) < 100

/** Prisma where fragment for the same rule. */
export const openDemandWhere = () => ({
  status: { notIn: [...OPEN_DEMAND_TERMINAL_STATUSES] },
  fulfillmentPercentage: { lt: 100 },
})

export const DemandSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  title: z.string().max(255),
  description: z.string().optional().nullable(),
  status: z.nativeEnum(DemandStatus).default(DemandStatus.DRAFT),
  source: z.nativeEnum(DemandSource).default(DemandSource.MANUAL),
  priority: z.nativeEnum(DemandPriority).default(DemandPriority.MEDIUM),
  confidence: z.number().int().min(0).max(100).default(100),
  requestedById: z.string().uuid(),
  ownerId: z.string().uuid().optional().nullable(),
  projectId: z.string().uuid().optional().nullable(),
  dealId: z.string().uuid().optional().nullable(),
  entityId: z.string().uuid().optional().nullable(),
  roleId: z.string().uuid().optional().nullable(),
  // Catalog-skill requirements; the matcher scores on skillId. (Skill ids are
  // cuids, not uuids.) Pre-picker demands stored plain name strings — the
  // matcher resolves those against the catalog at match time.
  skills: z
    .array(
      z.object({
        skillId: z.string().min(1),
        minProficiency: z.number().int().min(1).max(5).optional(),
      }),
    )
    .default([]),
  seniorityLevel: z.string().optional().nullable(),
  workerType: z.nativeEnum(DemandWorkerType).optional().nullable(),
  hoursPerWeek: decimalSchema,
  startDate: z.date(),
  endDate: z.date().optional().nullable(),
  totalEstimatedHours: decimalSchema.optional().nullable(),
  billRateContext: z.any().optional().nullable(),
  budgetImpact: z.any().optional().nullable(),
  fulfilledHoursPerWeek: decimalSchema.default(0 as any),
  fulfillmentPercentage: decimalSchema.default(0 as any),
  notes: z.string().optional().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  closedAt: z.date().optional().nullable(),
  closedReason: z.string().optional().nullable(),
})

export type Demand = z.infer<typeof DemandSchema>

export const CreateDemandSchema = DemandSchema.pick({
  title: true,
  source: true,
  priority: true,
  confidence: true,
  projectId: true,
  dealId: true,
  entityId: true,
  roleId: true,
  skills: true,
  seniorityLevel: true,
  workerType: true,
  hoursPerWeek: true,
  startDate: true,
  endDate: true,
  totalEstimatedHours: true,
  billRateContext: true,
  budgetImpact: true,
  description: true,
  notes: true,
}).partial({ confidence: true, priority: true, skills: true })

export type CreateDemand = z.infer<typeof CreateDemandSchema>

export const UpdateDemandSchema = CreateDemandSchema.partial().extend({
  // Owner is assignable after creation (triage requires one); create always
  // starts unowned, so ownerId lives on update only.
  ownerId: z.string().uuid().optional().nullable(),
})
export type UpdateDemand = z.infer<typeof UpdateDemandSchema>

export const DemandMetricsSchema = z.object({
  totalOpen: z.number(),
  confirmedHoursPerWeek: z.number(),
  pipelineHoursPerWeek: z.number(),
  weightedTotalHoursPerWeek: z.number(),
  unfulfilledHoursPerWeek: z.number(),
  averageTimeToFulfillDays: z.number().nullable(),
})

export type DemandMetrics = z.infer<typeof DemandMetricsSchema>

export const ProjectStaffingWeeklyBreakdownSchema = z.object({
  weekStart: z.string(),
  weekEnd: z.string(),
  plannedHours: z.number().nullable(),
  actualHours: z.number(),
  varianceHours: z.number().nullable(),
  variancePercent: z.number().nullable(),
  status: z.enum(['ON_TRACK', 'BEHIND', 'UPCOMING']),
})

export const ProjectStaffingAllocationRowSchema = z.object({
  allocationId: z.string(),
  memberId: z.string(),
  memberName: z.string(),
  avatarUrl: z.string().nullable().optional(),
  workerType: z.string().nullable().optional(),
  roleId: z.string().nullable().optional(),
  roleName: z.string().nullable().optional(),
  /** Allocation lifecycle state (CONFIRMED, TENTATIVE, PLACEHOLDER). */
  status: z.string().optional(),
  allocationPercent: z.number().nullable().optional(),
  /** Owning project; null for a client-level allocation on the entity tab. */
  projectId: z.string().nullable().optional(),
  projectName: z.string().nullable().optional(),
  startDate: z.string(),
  endDate: z.string(),
  hoursPerWeek: z.number().nullable(),
  totalPlannedHours: z.number().nullable(),
  actualHours: z.number(),
  varianceHours: z.number().nullable(),
  variancePercent: z.number().nullable(),
  effectiveBillRate: z.number().nullable().optional(),
  /** Which cascade layer produced effectiveBillRate (ResolvedFrom); null when masked. */
  billRateSource: z.string().nullable().optional(),
  hasRoleRateMismatch: z.boolean(),
  weeklyBreakdown: z.array(ProjectStaffingWeeklyBreakdownSchema),
})

/** One open demand row for the Open Staffing Requests card on either tab. */
export const StaffingDemandRowSchema = z.object({
  id: z.string(),
  title: z.string(),
  status: z.string(),
  hoursPerWeek: z.number(),
  startDate: z.string().nullable(),
  fulfilledHoursPerWeek: z.number(),
  fulfillmentPercentage: z.number(),
  projectId: z.string().nullable().optional(),
  projectName: z.string().nullable().optional(),
  entityId: z.string().nullable().optional(),
})
export type StaffingDemandRow = z.infer<typeof StaffingDemandRowSchema>

export const StaffingPlanningSummarySchema = z.object({
  teamSize: z.number(),
  confirmedHoursPerWeek: z.number(),
  totalHoursPerWeek: z.number(),
  actualHoursThisPeriod: z.number(),
  plannedVsActualPercent: z.number().nullable(),
  /** Open demands on this record (see isOpenDemand). */
  openStaffingRequestCount: z.number(),
  conflictCount: z.number(),
})
export type StaffingPlanningSummary = z.infer<
  typeof StaffingPlanningSummarySchema
>

/** Which money columns the server left populated for this viewer. */
export const StaffingColumnVisibilitySchema = z.object({
  billRate: z.boolean(),
  costAmount: z.boolean(),
})
export type StaffingColumnVisibility = z.infer<
  typeof StaffingColumnVisibilitySchema
>

export type StaffingAllocationRow = z.infer<
  typeof ProjectStaffingAllocationRowSchema
>

export const ProjectStaffingUnallocatedActualSchema = z.object({
  memberId: z.string(),
  memberName: z.string(),
  actualHours: z.number(),
  totalCostAmount: z.number().nullable().optional(),
})

export const ProjectStaffingResponseSchema = z.object({
  projectId: z.string(),
  statusTier: TimeEntryStatusTierSchema,
  summary: StaffingPlanningSummarySchema,
  allocations: z.array(ProjectStaffingAllocationRowSchema),
  /** Open demands on this project (Request Staffing creates these). */
  demands: z.array(StaffingDemandRowSchema),
  unallocatedActuals: z.array(ProjectStaffingUnallocatedActualSchema),
  /** Optional for older clients; effectiveBillRate is null when billRate is false. */
  columnVisibility: StaffingColumnVisibilitySchema.optional(),
})

export type ProjectStaffingResponse = z.infer<
  typeof ProjectStaffingResponseSchema
>

// =====================
// Record roster (project / entity)
// =====================
// One row per person unioned across allocations, record membership, logged
// time and task assignments. Plan/Access/Work columns are current state; only
// Activity is windowed. Reconciliation flags are computed server-side.

export const ROSTER_DORMANT_DAYS = 60

export const RosterWindowPresetSchema = z.enum([
  'this-period',
  'last-30',
  'last-90',
  'all',
  'custom',
])
export type RosterWindowPreset = z.infer<typeof RosterWindowPresetSchema>

export const RosterSourceSchema = z.enum([
  'ALLOCATION',
  'MEMBER',
  'TIME',
  'TASKS',
])
export type RosterSource = z.infer<typeof RosterSourceSchema>

export const RosterReconciliationFlagSchema = z.enum([
  'ALLOCATION_ENDED',
  'NOT_PLANNED',
  'NO_RECORD_ACCESS',
  'ASSIGNED_NOT_ON_RECORD',
  'DORMANT',
])
export type RosterReconciliationFlag = z.infer<
  typeof RosterReconciliationFlagSchema
>

export const RosterWindowSchema = z.object({
  preset: RosterWindowPresetSchema,
  /** YYYY-MM-DD; null for the `all` window. */
  start: z.string().nullable(),
  end: z.string().nullable(),
})
export type RosterWindow = z.infer<typeof RosterWindowSchema>

export const RosterPersonSchema = z.object({
  /** memberId, else `user:<userId>` for authors no longer in the org. */
  key: z.string(),
  memberId: z.string().nullable(),
  userId: z.string().nullable(),
  name: z.string(),
  avatarUrl: z.string().nullable(),
  workerType: z.string().nullable(),
  sources: z.array(RosterSourceSchema),
  /** Primary allocation: active today, else the latest by end date. */
  allocation: z
    .object({
      id: z.string(),
      status: z.nativeEnum(AllocationStatus),
      roleId: z.string().nullable(),
      roleName: z.string().nullable(),
      hoursPerWeek: z.number().nullable(),
      startDate: z.string(),
      endDate: z.string(),
      isEnded: z.boolean(),
      count: z.number(),
    })
    .nullable(),
  member: z
    .object({
      id: z.string(),
      roleKey: z.string(),
      status: z.string(),
      jobRoleName: z.string().nullable(),
      /** Project the membership row belongs to; null for the record itself. */
      projectId: z.string().nullable(),
    })
    .nullable(),
  tasks: z.object({
    openCount: z.number(),
    overdueCount: z.number(),
  }),
  activity: z.object({
    hoursInWindow: z.number(),
    /** All-time most recent entry date; never windowed. */
    lastLoggedAt: z.string().nullable(),
    plannedHoursInWindow: z.number().nullable(),
    varianceHours: z.number().nullable(),
    /** Omitted (undefined) when the viewer cannot see bill data. */
    billRate: z.number().nullable().optional(),
    /** Omitted (undefined) when the viewer cannot see cost data. */
    costInWindow: z.number().nullable().optional(),
  }),
  flags: z.array(RosterReconciliationFlagSchema),
  /** Average hrs/week in the window, for the "Not planned" prompt. */
  suggestedHoursPerWeek: z.number().nullable(),
})
export type RosterPerson = z.infer<typeof RosterPersonSchema>

export const RosterColumnVisibilitySchema = z.object({
  billRate: z.boolean(),
  costAmount: z.boolean(),
})
export type RosterColumnVisibility = z.infer<
  typeof RosterColumnVisibilitySchema
>

export const ProjectRosterResponseSchema = z.object({
  projectId: z.string(),
  statusTier: TimeEntryStatusTierSchema,
  window: RosterWindowSchema,
  people: z.array(RosterPersonSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  columnVisibility: RosterColumnVisibilitySchema,
})
export type ProjectRosterResponse = z.infer<typeof ProjectRosterResponseSchema>

export const EntityRosterPersonSchema = RosterPersonSchema.extend({
  projectIds: z.array(z.string()),
})
export type EntityRosterPerson = z.infer<typeof EntityRosterPersonSchema>

export const EntityRosterProjectSchema = z.object({
  id: z.string(),
  name: z.string(),
  isArchived: z.boolean(),
})
export type EntityRosterProject = z.infer<typeof EntityRosterProjectSchema>

export const EntityRosterResponseSchema = z.object({
  entityId: z.string(),
  statusTier: TimeEntryStatusTierSchema,
  window: RosterWindowSchema,
  people: z.array(EntityRosterPersonSchema),
  /** Every child project referenced by `people[].projectIds`, archived included. */
  projects: z.array(EntityRosterProjectSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  columnVisibility: RosterColumnVisibilitySchema,
})
export type EntityRosterResponse = z.infer<typeof EntityRosterResponseSchema>

export const ProjectTimeEntryRowSchema = z.object({
  id: z.string(),
  entryDate: z.string(),
  memberId: z.string(),
  memberName: z.string(),
  roleId: z.string().nullable().optional(),
  roleName: z.string().nullable().optional(),
  workItem: z.object({
    moduleType: z.enum(['PROJECT', 'TASK', 'TASK_GROUP']),
    moduleId: z.string(),
    moduleName: z.string(),
  }),
  description: z.string().nullable().optional(),
  duration: z.number(),
  billable: z.boolean(),
  status: z.string(),
  authorization: z
    .object({ id: z.string(), label: z.string() })
    .nullable()
    .optional(),
  billRate: z.number().nullable().optional(),
  costRate: z.number().nullable().optional(),
  totalBillableAmount: z.number().nullable().optional(),
  totalCostAmount: z.number().nullable().optional(),
})

export const ProjectTimeEntriesResponseSchema = z.object({
  projectId: z.string(),
  statusTier: TimeEntryStatusTierSchema,
  columnVisibility: z.object({
    billRate: z.boolean(),
    costRate: z.boolean(),
    billAmount: z.boolean(),
    costAmount: z.boolean(),
  }),
  data: z.array(ProjectTimeEntryRowSchema),
  total: z.number(),
  totals: z.object({
    totalHours: z.number(),
    billableHours: z.number(),
    nonBillableHours: z.number(),
    totalBillableAmount: z.number().optional(),
    totalCostAmount: z.number().optional(),
  }),
})

export type ProjectTimeEntriesResponse = z.infer<
  typeof ProjectTimeEntriesResponseSchema
>

export const ProjectOverviewSummaryResponseSchema = z.object({
  // Omitted server-side for users without the Staffing tab gate
  // (Project manage + Financials read); the widgets are gated the same way.
  staffingSummary: z
    .object({
      teamSize: z.number(),
      confirmedHoursPerWeek: z.number(),
      totalHoursPerWeek: z.number(),
      openStaffingRequestCount: z.number(),
      /** Distinct people who logged time on the project in the last 30 days. */
      activePeopleLast30: z.number(),
    })
    .optional(),
  recentTime: z.array(
    z.object({
      id: z.string(),
      entryDate: z.string(),
      userId: z.string().optional(),
      memberName: z.string(),
      roleName: z.string().nullable().optional(),
      moduleType: z.string().nullable().optional(),
      moduleId: z.string().nullable().optional(),
      moduleName: z.string(),
      duration: z.number(),
    }),
  ),
  // Omitted server-side with staffingSummary: the alerts are staffing data.
  alerts: z
    .array(
      z.object({
        type: z.enum([
          'OVERALLOCATION_CONFLICT',
          'UNFULFILLED_STAFFING_REQUEST',
          'UNFULFILLED_DEMAND',
        ]),
        label: z.string(),
        count: z.number(),
        href: z.string(),
      }),
    )
    .optional(),
  // Omitted server-side for users without cost-data visibility
  budgetHealth: z
    .object({
      status: z.enum(['GREEN', 'AMBER', 'RED']).nullable(),
      actualSpend: z.string().nullable(),
      currentForecast: z.string().nullable(),
      burnRate: z.string().nullable(),
      budgetOwnership: z.enum(['PROJECT', 'CLIENT', 'ONE_OFF']).optional(),
      href: z.string(),
    })
    .nullable()
    .optional(),
  // Omitted server-side for users without bill+cost visibility
  marginSnapshot: z
    .object({
      marginPercent: z.string().nullable(),
      trend: z.enum(['UP', 'DOWN', 'FLAT']).nullable(),
      lastPeriodMarginPercent: z.string().nullable(),
      budgetOwnership: z.enum(['PROJECT', 'CLIENT', 'ONE_OFF']).optional(),
      href: z.string(),
    })
    .nullable()
    .optional(),
})

export type ProjectOverviewSummaryResponse = z.infer<
  typeof ProjectOverviewSummaryResponseSchema
>

// =====================
// Member Earnings
// =====================

// Payroll config snapshot for audit trail (captures exact config at calculation time)
export const PayrollConfigSnapshotSchema = z.object({
  configId: z.string().uuid(),
  version: z.number(),
  versionCreatedAt: z.string(), // ISO date string
  // Config settings at time of calculation
  defaultHoursPerWeek: z.number(),
  defaultHoursPerPayPeriod: z.number(),
  payPeriodsPerYear: z.number(),
  defaultWeeksPerYear: z.number(),
  currency: z.string(),
  region: z.string(),
  // Tax items snapshot
  taxItems: z.array(
    z.object({
      id: z.string().uuid(),
      code: z.string(),
      name: z.string(),
      taxType: z.string(),
      ratePercent: z.number().nullable(),
      flatAmount: z.number().nullable(),
      wageBaseLimit: z.number().nullable(),
    }),
  ),
  // Benefit templates snapshot
  benefits: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      benefitType: z.string(),
      employerAmount: z.number().nullable(),
      employerPercent: z.number().nullable(),
      employeeAmount: z.number().nullable(),
      employeePercent: z.number().nullable(),
      matchPercent: z.number().nullable(),
      matchUpToPercent: z.number().nullable(),
    }),
  ),
})

export type PayrollConfigSnapshot = z.infer<typeof PayrollConfigSnapshotSchema>

// Payroll breakdown schema for earnings (stored for audit trail)
export const EarningsPayrollBreakdownSchema = z.object({
  // Compensation reference
  compensationId: z.string().uuid().optional(),
  effectiveHourlyRate: z.number().optional(),

  // Employee deductions
  employeeTaxes: z
    .array(
      z.object({
        taxItemId: z.string().uuid().optional(),
        name: z.string(),
        code: z.string(),
        amount: z.number(),
        rate: z.number().optional(),
      }),
    )
    .default([]),
  employeeBenefits: z
    .array(
      z.object({
        benefitId: z.string().uuid().optional(),
        name: z.string(),
        amount: z.number(),
      }),
    )
    .default([]),
  totalEmployeeDeductions: z.number().default(0),

  // Employer contributions
  employerTaxes: z
    .array(
      z.object({
        taxItemId: z.string().uuid().optional(),
        name: z.string(),
        code: z.string(),
        amount: z.number(),
        rate: z.number().optional(),
      }),
    )
    .default([]),
  employerBenefits: z
    .array(
      z.object({
        benefitId: z.string().uuid().optional(),
        name: z.string(),
        amount: z.number(),
      }),
    )
    .default([]),
  totalEmployerContributions: z.number().default(0),

  // Summary
  netPay: z.number().default(0),
  totalEmployerCost: z.number().default(0),

  // YTD (Year-to-Date) summary
  ytdSummary: z
    .object({
      ytdGrossEarnings: z.number(),
      ytdNetEarnings: z.number(),
      ytdTotalHours: z.number(),
      ytdTotalEmployeeTaxes: z.number(),
      ytdTotalEmployerTaxes: z.number(),
      ytdTotalEmployeeBenefits: z.number(),
      ytdTotalEmployerBenefits: z.number(),
      periodsCounted: z.number(),
      // Wage base limit status for each applicable tax
      wageBaseLimitStatus: z
        .array(
          z.object({
            code: z.string(),
            name: z.string(),
            limit: z.number(),
            ytdWages: z.number(),
            remaining: z.number(),
            percentUsed: z.number(),
            isReached: z.boolean(),
          }),
        )
        .default([]),
    })
    .optional(),

  // Config version for audit trail (simple reference)
  payrollConfigVersion: z.string().optional(),
  // Full config snapshot for complete audit trail
  configSnapshot: PayrollConfigSnapshotSchema.optional(),
  calculatedAt: z.date().optional(),
})

export type EarningsPayrollBreakdown = z.infer<
  typeof EarningsPayrollBreakdownSchema
>

export const MemberEarningsSchema = z.object({
  id: z.string().uuid(),
  memberId: z.string().uuid(),
  organizationId: z.string().uuid(),
  periodStart: z.date(),
  periodEnd: z.date(),
  totalHours: decimalSchema,
  billableHours: decimalSchema,
  grossEarnings: decimalSchema,
  adjustments: decimalSchema.default(0),
  netEarnings: decimalSchema,
  currency: z
    .string()
    .regex(/^[A-Z]{3}$/)
    .nullable()
    .optional(),
  status: z.nativeEnum(EarningsStatus).default(EarningsStatus.PENDING),
  approvedAt: z.date().optional().nullable(),
  approvedBy: z.string().uuid().optional().nullable(),
  paidAt: z.date().optional().nullable(),
  paymentRef: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
  breakdown: z.any().default([]),
  // Enhanced payroll breakdown with tax/benefit details
  payrollBreakdown: EarningsPayrollBreakdownSchema.optional().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  member: z.any().optional(),
  approver: z.any().optional(),
})

export type MemberEarnings = z.infer<typeof MemberEarningsSchema>

export const CreateMemberEarningsSchema = MemberEarningsSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  member: true,
  approver: true,
})

export type CreateMemberEarnings = z.infer<typeof CreateMemberEarningsSchema>

export const UpdateMemberEarningsSchema =
  CreateMemberEarningsSchema.partial().omit({
    memberId: true,
    organizationId: true,
  })

export type UpdateMemberEarnings = z.infer<typeof UpdateMemberEarningsSchema>

// =====================
// Payroll Run
// =====================

export type PayrollCsvFormat = 'standard' | 'adp' | 'gusto'

export interface PayrollRunStatusBucket {
  count: number
  grossEarnings: number
  netEarnings: number
}

export interface PayrollRunSummary {
  periodStart: string
  periodEnd: string
  currency: string | null
  byStatus: {
    pending: PayrollRunStatusBucket
    approved: PayrollRunStatusBucket
    paid: PayrollRunStatusBucket
    rejected: PayrollRunStatusBucket
  }
  totalMembers: number
  totalGrossEarnings: number
  totalNetEarnings: number
  totalEmployerCost: number
  totalHours: number
  readyForApproval: number
  readyForPayment: number
}

export interface PayrollRunProcessRow {
  memberId: string
  memberName: string
  status: 'created' | 'updated' | 'skipped' | 'error'
  earningsId?: string
  currency?: string | null
  grossEarnings?: number
  netEarnings?: number
  totalHours?: number
  error?: string
}

export interface PayrollRunProcessResult {
  runId: string
  periodStart: string
  periodEnd: string
  processedAt: string
  summary: {
    totalMembers: number
    membersProcessed: number
    membersSkipped: number
    currency: string | null
    totalGrossEarnings: number | null
    totalNetEarnings: number | null
    totalEmployerCost: number | null
    totalHours: number
    errors: number
  }
  results: PayrollRunProcessRow[]
}

export interface BulkEarningsActionRow {
  earningsId: string
  memberId: string
  memberName: string
  status: string
  error?: string
}

export interface BulkApproveEarningsResult {
  totalApproved: number
  totalSkipped: number
  totalErrors: number
  results: BulkEarningsActionRow[]
}

export interface BulkPayEarningsResult {
  totalPaid: number
  totalSkipped: number
  totalErrors: number
  totalAmount: number
  currency: string | null
  results: (BulkEarningsActionRow & { amount: number })[]
}

export interface BulkUnapproveEarningsResult {
  totalUnapproved: number
  totalSkipped: number
  totalErrors: number
  results: BulkEarningsActionRow[]
}

// =====================
// Member Allocation
// =====================

export const MemberAllocationSchema = z.object({
  id: z.string().uuid(),
  memberId: z.string().uuid(),
  organizationId: z.string().uuid(),
  projectId: z.string().uuid().optional().nullable(),
  deliverableId: z.string().uuid().optional().nullable(),
  entityId: z.string().uuid().optional().nullable(),
  dealId: z.string().uuid().optional().nullable(),
  roleId: z.string().uuid().optional().nullable(),
  startDate: z.date(),
  endDate: z.date(),
  allocationPercent: decimalSchema,
  hoursPerWeek: decimalSchema.optional().nullable(),
  allocationType: z.nativeEnum(AllocationType).default(AllocationType.PROJECT),
  status: z.nativeEnum(AllocationStatus).default(AllocationStatus.CONFIRMED),
  statusChangedAt: z.date().optional().nullable(),
  statusChangedBy: z.string().uuid().optional().nullable(),
  statusReason: z.string().optional().nullable(),
  priority: z.number().int().default(0),
  notes: z.string().optional().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  member: z.any().optional(),
  project: z.any().optional(),
  entity: z.any().optional(),
  deal: z.any().optional(),
})

export type MemberAllocation = z.infer<typeof MemberAllocationSchema>

export interface EnrichedMemberAllocation extends MemberAllocation {
  allocationRole?: {
    id: string
    name: string
  } | null
  demand?: {
    id: string
    title: string
    source: DemandSource
    status: DemandStatus
  } | null
  latestHistory?: {
    id: string
    action: AllocationHistoryAction
    changedAt: Date | string
    reason?: string | null
    changedBy?: {
      id: string
      name: string | null
    } | null
  } | null
  conflictSummary?: {
    hasConflict: boolean
    conflictCount: number
    highestTotalPercent: number | null
    highestOverallocation: number | null
  }
  forecastIncluded?: boolean
  plannerHref?: string | null
  forecastHref?: string | null
}

// Plan 4: Allocation history record
export const AllocationHistoryRecordSchema = z.object({
  id: z.string().uuid(),
  allocationId: z.string().uuid(),
  action: z.nativeEnum(AllocationHistoryAction),
  previousValues: z.record(z.any()).optional().nullable(),
  newValues: z.record(z.any()).optional().nullable(),
  changedBy: z
    .object({
      id: z.string().uuid(),
      name: z.string().optional().nullable(),
    })
    .optional()
    .nullable(),
  changedAt: z.date(),
  reason: z.string().optional().nullable(),
  staffingDecisionId: z.string().uuid().optional().nullable(),
})

export type AllocationHistoryRecord = z.infer<
  typeof AllocationHistoryRecordSchema
>

// Plan 4: Staffing decision record
export const StaffingDecisionRecordSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  reason: z.string().optional().nullable(),
  decidedBy: z.string().uuid(),
  decidedAt: z.date(),
  linkedAllocationChanges: z.array(z.string().uuid()),
  createdAt: z.date(),
  updatedAt: z.date(),
})

export type StaffingDecisionRecord = z.infer<
  typeof StaffingDecisionRecordSchema
>

export const CreateMemberAllocationSchema = MemberAllocationSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  member: true,
  project: true,
  entity: true,
  deal: true,
})

export type CreateMemberAllocation = z.infer<
  typeof CreateMemberAllocationSchema
>

export const UpdateMemberAllocationSchema =
  CreateMemberAllocationSchema.partial().omit({
    memberId: true,
    organizationId: true,
  })

export type UpdateMemberAllocation = z.infer<
  typeof UpdateMemberAllocationSchema
>

// =====================
// Member Skill
// =====================

export const MemberSkillSchema = z.object({
  id: z.string(),
  memberId: z.string().uuid(),
  skillId: z.string(),
  proficiency: z.number().int().min(1).max(5).default(3),
  yearsExperience: z.number().default(0),
  lastUsed: z.date().optional().nullable(),
  certified: z.boolean().default(false),
  certificationDate: z.date().optional().nullable(),
  notes: z.string().optional().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  member: z.any().optional(),
  skill: z.any().optional(),
})

export type MemberSkill = z.infer<typeof MemberSkillSchema>

export const CreateMemberSkillSchema = MemberSkillSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  member: true,
  skill: true,
})

export type CreateMemberSkill = z.infer<typeof CreateMemberSkillSchema>

export const UpdateMemberSkillSchema = CreateMemberSkillSchema.partial().omit({
  memberId: true,
  skillId: true,
})

export type UpdateMemberSkill = z.infer<typeof UpdateMemberSkillSchema>

// =====================
// Workload Summary (Plan 9f T5 — shared source of truth)
// =====================

export type WorkloadTargetType =
  | 'PROJECT'
  | 'CLIENT'
  | 'DEAL'
  | 'INTERNAL'
  | 'PTO'

export interface WorkloadWeekPoint {
  /** ISO date — week bucket start, planner week convention (org weekStartsOn). */
  weekStart: string
  /** Capacity after schedule exceptions / holidays / confirmed PTO. */
  capacityHours: number
  /** Allocated hours from non-PTO, non-ARCHIVED allocations overlapping the week. */
  forecastHours: number
  /** Subset of forecastHours from TENTATIVE + PLACEHOLDER allocations. */
  tentativeForecastHours: number
  /** Null for future weeks — no actuals regardless of stray entries. */
  actualHours: number | null
  forecastUtilPct: number
  actualUtilPct: number | null
  /** Logged but not yet submitted (DRAFT) hours; not part of actuals. */
  draftHours?: number | null
  /** draftHours as a share of capacity, for the chart's lighter segment. */
  draftUtilPct?: number | null
  availableHours: number
}

export interface WorkloadAvailabilitySummary {
  /** Computed over future weeks in the requested period; all-null when the period is entirely past. */
  avgAvailableHoursPerWeek: number | null
  nextAvailableWeek: string | null
  fullyBookedThrough: string | null
}

export interface WorkloadSummary {
  billableUtilPct: number | null
  totalUtilPct: number | null
  /** Period forecast hours / period capacity hours. Null when capacity is unknown. */
  forecastUtilPct: number | null
  weightedMarginPct: number | null
  /** Plan 9f: true when TOTAL_COST projects contributed to the weighted margin (shows asterisk footnote). */
  marginIncludesTotalCost?: boolean
  marginIndicator: 'green' | 'yellow' | 'red' | null
  revenueAttainment: { actual: number; target: number; pct: number } | null
  accountLoad: { current: number; max: number | null } | null
  burnoutRisk: {
    level: string
    streakWeeks: number
    avgOverTargetPct: number
    recentWeeks: Array<{ weekStart: string; totalUtilization: number }>
  } | null
  staleness: { lastCalculatedAt: string | null }
}

// =====================
// Member Utilization
// =====================

export const MemberUtilizationSchema = z.object({
  id: z.string(),
  memberId: z.string().uuid(),
  organizationId: z.string().uuid(),
  periodStart: z.date(),
  periodType: z.nativeEnum(PeriodType).default(PeriodType.WEEK),
  totalHours: decimalSchema.default(0),
  billableHours: decimalSchema.default(0),
  allocatedHours: decimalSchema.default(0),
  utilizationRate: decimalSchema.default(0),
  billableRate: decimalSchema.default(0),
  totalCost: decimalSchema.default(0),
  totalRevenue: decimalSchema.default(0),
  // Plan 9a: expanded metrics
  availableCapacity: decimalSchema.default(0),
  productiveHours: decimalSchema.default(0),
  nonBillableHours: decimalSchema.default(0),
  targetUtilization: decimalSchema.default(0),
  billableUtilization: decimalSchema.default(0),
  productiveUtilization: decimalSchema.default(0),
  targetVariance: decimalSchema.default(0),
  totalUtilization: decimalSchema.default(0),
  billableRevenue: decimalSchema.default(0),
  revenueTarget: decimalSchema.nullable().optional(),
  revenueAttainment: decimalSchema.nullable().optional(),
  activeProjectCount: z.number().int().default(0),
  maxProjectCount: z.number().int().nullable().optional(),
  overTargetStreakWeeks: z.number().int().default(0),
  burnoutRiskLevel: z
    .enum(['NONE', 'WATCH', 'ELEVATED', 'HIGH'])
    .default('NONE'),
  totalRevenueOv: decimalSchema.default(0),
  totalCostOv: decimalSchema.default(0),
  totalRevenueFa: decimalSchema.default(0),
  totalCostFa: decimalSchema.default(0),
  // Plan 9c: per-project grain fields
  projectId: z.string().uuid().optional().nullable(),
  weightedMarginPct: decimalSchema.nullable().optional(),
  // Plan 9f: flag indicating whether TOTAL_COST projects were included in the weighted margin
  weightedMarginIncludesTotalCost: z.boolean().default(false).optional(),
  calculatedAt: z.date().optional().nullable(),
  calculationTrigger: z
    .nativeEnum(UtilizationCalculationTrigger)
    .optional()
    .nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  member: z.any().optional(),
})

export type MemberUtilization = z.infer<typeof MemberUtilizationSchema>

export const CreateMemberUtilizationSchema = MemberUtilizationSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  member: true,
})

export type CreateMemberUtilization = z.infer<
  typeof CreateMemberUtilizationSchema
>

// =====================
// Forecast Types
// =====================

export enum ForecastType {
  CAPACITY = 'CAPACITY',
  REVENUE = 'REVENUE',
  UTILIZATION = 'UTILIZATION',
}

export enum ScenarioStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
}

export enum Granularity {
  WEEK = 'WEEK',
  MONTH = 'MONTH',
  QUARTER = 'QUARTER',
}

// Capacity Forecast Point
export const CapacityForecastPointSchema = z.object({
  date: z.string(),
  periodStart: z.string().optional(),
  periodEnd: z.string().optional(),
  totalCapacity: z.number(),
  allocatedHours: z.number(),
  availableHours: z.number(),
  utilizationPercent: z.number(),
  // A1 (Plan 09 T1.1): best-case demand if every live deal (probability > 0)
  // lands at FULL staffing — surfaced only when dealChanges are present, so the
  // expected allocatedHours/utilizationPercent above stay untouched (deal
  // staffing is unconfirmed). Existing members' deal hours add to demand without
  // adding capacity, so over-allocation (>100%) surfaces; TBD staff add both.
  bestCaseAllocatedHours: z.number().optional(),
  bestCaseTotalCapacity: z.number().optional(),
  bestCaseUtilizationPercent: z.number().optional(),
  memberBreakdown: z.array(
    z.object({
      memberId: z.string().uuid(),
      memberName: z.string(),
      allocated: z.number(),
      available: z.number(),
    }),
  ),
  contributors: z.lazy(() => ForecastPeriodContributorSchema).optional(),
})

export type CapacityForecastPoint = z.infer<typeof CapacityForecastPointSchema>

export const ForecastPeriodAllocationRowSchema = z.object({
  allocationId: z.string().uuid(),
  memberId: z.string().uuid().nullable(),
  memberName: z.string(),
  targetType: z.string(),
  targetId: z.string().uuid().nullable(),
  targetName: z.string(),
  roleName: z.string().nullable().optional(),
  status: z.string(),
  allocatedHours: z.number().optional(),
  projectedRevenue: z.number().optional(),
  projectedCost: z.number().nullable().optional(),
  projectedProfit: z.number().nullable().optional(),
})

export type ForecastPeriodAllocationRow = z.infer<
  typeof ForecastPeriodAllocationRowSchema
>

export const ForecastPeriodContributorSchema = z.object({
  allocationIds: z.array(z.string().uuid()),
  allocationCount: z.number(),
  statusCounts: z.record(z.number()),
  sourceCounts: z.object({
    named: z.number(),
    placeholder: z.number(),
    project: z.number(),
    entity: z.number(),
    deal: z.number(),
  }),
  projectIds: z.array(z.string().uuid()),
  entityIds: z.array(z.string().uuid()),
  allocations: z.array(ForecastPeriodAllocationRowSchema).optional(),
  hasMoreContributors: z.boolean().optional(),
})

export type ForecastPeriodContributor = z.infer<
  typeof ForecastPeriodContributorSchema
>

// Member Cost Breakdown (for forecasting)
export const MemberCostBreakdownSchema = z.object({
  memberId: z.string(),
  memberName: z.string(),
  hours: z.number(),
  // Plan 6b: null when cost cascade unresolved (placeholder rows)
  grossPay: z.number().nullable(),
  employerTaxes: z.number().nullable(),
  employerBenefits: z.number().nullable(),
  totalCost: z.number().nullable(), // null when cost cascade unresolved
  billableRevenue: z.number(),
  profit: z.number().nullable(), // null when totalCost is null
  // Plan 6b: true iff cost was estimated via ratio (not yet implemented — always false in this slice)
  costEstimated: z.boolean().optional(),
  margin: z.number().nullable().optional(),
})

export type MemberCostBreakdown = z.infer<typeof MemberCostBreakdownSchema>

// ---------------------------------------------------------------------------
// Plan 05 — per-allocation revenue/cost attribution row. The finest grain the
// engine emits: one row per (member × target) revenue/cost contribution within
// a period, carrying enough context (member, project, entity, deal) to pivot
// results by ANY dimension downstream without double counting. Every dollar of
// projectedRevenue/Cost in a period lands in exactly one row, so per-dimension
// sums reconcile to the org total (asserted in the engine spec).
// ---------------------------------------------------------------------------
export const RevenueBreakdownRowSchema = z.object({
  memberId: z.string().nullable(),
  memberName: z.string(),
  projectId: z.string().nullable(),
  projectName: z.string().nullable(),
  entityId: z.string().nullable(),
  entityName: z.string().nullable(),
  dealId: z.string().nullable(),
  dealName: z.string().nullable(),
  revenue: z.number(),
  cost: z.number(),
  hours: z.number(),
  // Plan 05: payroll base (gross pay) for this contribution. Used only to
  // distribute org-level employer-cost adjustments (tax/benefit/comp changes)
  // back into rows so per-entity cost reconciles to projectedCost. Transient —
  // the per-period rows are stripped before the results blob is persisted.
  gross: z.number().optional(),
})

export type RevenueBreakdownRow = z.infer<typeof RevenueBreakdownRowSchema>

/**
 * Plan 05 — per-period deal portfolio accounting. The main forecast applies the
 * EXPECTED (probability-weighted) deal contribution; these carry the expected
 * and full (certain-win) values so the service can derive best/expected/worst
 * analytically (see ScenarioPortfolio).
 */
export const DealPortfolioMetaSchema = z.object({
  expectedRevenue: z.number(),
  fullRevenue: z.number(),
  expectedCost: z.number(),
  fullCost: z.number(),
  // T10 — the SAME period's expected deal contribution computed from the
  // NON-headline probability source, so the UI can show "headline $X (rep) vs
  // $Y (historical)" side by side without re-running the engine. Equals the
  // headline expected when a deal has no alternate signal (e.g. derived withheld
  // at cold-start), so it never invents a divergence. Only emitted when deals
  // are present.
  alternateExpectedRevenue: z.number().optional(),
  alternateExpectedCost: z.number().optional(),
  // Which source drove the headline (`expectedRevenue`); the alternate is the
  // other one.
  headlineProbabilityDriver: z.enum(['derived', 'rep']).optional(),
})

export type DealPortfolioMeta = z.infer<typeof DealPortfolioMetaSchema>

// Revenue Forecast Point
export const RevenueForecastPointSchema = z.object({
  currency: z.string().optional(),
  date: z.string(),
  periodStart: z.string().optional(),
  periodEnd: z.string().optional(),
  projectedRevenue: z.number(),
  projectedCost: z.number(),
  projectedProfit: z.number(),
  // AG3 + AG1 (Plan 09 T3): non-labor operating overhead + one-time onboarding
  // for this period. Kept OUT of projectedCost (which stays labor-only and
  // reconciles to the per-entity breakdown); projectedProfit nets it out so the
  // margin is a real P&L margin. Absent/0 ⇒ unchanged from labor-only behavior.
  otherCosts: z.number().optional(),
  confidence: z.number(),
  // B5 fix (D2): the heuristic ±20% revenue band is no longer emitted; kept
  // optional for back-compat reads of already-persisted scenario results.
  upperBound: z.number().optional(),
  lowerBound: z.number().optional(),
  // Enhanced cost breakdown
  costBreakdown: z
    .object({
      grossPayroll: z.number(),
      employerTaxes: z.number(),
      employerBenefits: z.number(),
      totalEmployerCost: z.number(),
      // B4 (Plan 09 T2.1): additive comp-loaded basis. `grossPayroll`/
      // `totalEmployerCost` stay on the rate-card basis (they tie to revenue and
      // the per-member table); these expose the true comp-loaded employer cost
      // (base salary from the member's compensation record, not the rate card)
      // for the finance "true cost to the books" surface. Equal to the rate-card
      // fields when the member has no compensation record.
      trueGrossPayroll: z.number().optional(),
      trueEmployerCost: z.number().optional(),
    })
    .optional(),
  // Member-level breakdown for drill-down
  memberBreakdown: z.array(MemberCostBreakdownSchema).optional(),
  // Plan 6b D2b: true iff any placeholder in this period has unresolved cost
  hasUnresolvedCost: z.boolean().optional(),
  // Plan 6b D2b: sum of resolved placeholder costs (0 when all unresolved)
  estimatedPlaceholderCost: z.number().optional(),
  contributors: ForecastPeriodContributorSchema.optional(),
  // Plan 05: per-allocation revenue/cost attribution (pivot source). Stripped
  // from the persisted results blob after aggregation into `breakdowns`.
  breakdownRows: z.array(RevenueBreakdownRowSchema).optional(),
  // Plan 05: per-period deal portfolio accounting (see DealPortfolioMeta).
  dealPortfolioMeta: DealPortfolioMetaSchema.optional(),
})

export type RevenueForecastPoint = z.infer<typeof RevenueForecastPointSchema>

// Utilization Forecast Point
export const UtilizationForecastPointSchema = z.object({
  date: z.string(),
  projectedUtilization: z.number(),
  targetUtilization: z.number(),
  variance: z.number(),
  memberCount: z.number(),
})

export type UtilizationForecastPoint = z.infer<
  typeof UtilizationForecastPointSchema
>

export const ForecastTimelineMetaSchema = z.object({
  currency: z.string().optional(),
  range: z.object({
    requestedStart: z.string(),
    requestedEnd: z.string(),
    generatedStart: z.string(),
    generatedEnd: z.string(),
    months: z.number(),
    granularity: z.nativeEnum(Granularity),
  }),
  scope: z.object({
    entityIds: z.array(z.string().uuid()),
    projectIds: z.array(z.string().uuid()),
    entities: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
    projects: z.array(
      z.object({
        id: z.string().uuid(),
        name: z.string(),
        entityId: z.string().uuid().nullable().optional(),
        entityName: z.string().nullable().optional(),
      }),
    ),
  }),
  layers: z.array(
    z.object({
      key: z.enum(['confirmed', 'tentative', 'placeholder', 'custom']),
      statuses: z.array(z.string()),
      label: z.string(),
    }),
  ),
  assumptions: z.object({
    allocationHours: z.string(),
    rateResolution: z.string(),
    costResolution: z.string(),
    placeholderResolution: z.string(),
  }),
  freshness: z.object({
    generatedAt: z.string(),
    newestAllocationUpdatedAt: z.string().nullable(),
    newestRateUpdatedAt: z.string().nullable(),
    newestCompensationUpdatedAt: z.string().nullable(),
  }),
})

export type ForecastTimelineMeta = z.infer<typeof ForecastTimelineMetaSchema>

// ---------------------------------------------------------------------------
// Scenario assumptions — typed/versioned model (Scenarios overhaul, plan 03)
//
// Storage stays JSONB on ForecastScenario; "typed/versioned" is enforced at the
// APPLICATION layer via Zod + a `schemaVersion` key INSIDE the document (PRD
// §5.5 — no DB migration, no column). The base schema below is intentionally
// permissive so legacy (v0, version-less) rows can be read without throwing;
// `ScenarioAssumptionsV1Schema` is the strict write-time validator, and
// `normalizeAssumptions` is the pure back-compat read path that maps any legacy
// blob → V1 and stamps `schemaVersion: 1`.
// ---------------------------------------------------------------------------

/** Current assumptions document version. Lives inside the JSONB, not a column. */
export const SCENARIO_ASSUMPTIONS_SCHEMA_VERSION = 1 as const

/**
 * Typed home for the former `meta.review` side-channel. The review-flag feature
 * used to stuff `{ meta: { review: {...} } }` into the otherwise-untyped
 * assumptions blob (financials/tabs/scenarios.tsx); it is now a first-class,
 * validated field on the schema rather than an ad-hoc nested key.
 */
export const ScenarioReviewMetaSchema = z.object({
  flaggedAt: z.string(),
  flaggedBy: z.string().nullable(),
})

export type ScenarioReviewMeta = z.infer<typeof ScenarioReviewMetaSchema>

export const ScenarioAssumptionsMetaSchema = z.object({
  review: ScenarioReviewMetaSchema.optional(),
})

export type ScenarioAssumptionsMeta = z.infer<
  typeof ScenarioAssumptionsMetaSchema
>

// Scenario Assumptions
export const ScenarioAssumptionsSchema = z.object({
  // Document version + typed metadata. Optional on the base schema so legacy
  // (version-less) rows still parse; the V1 schema below makes schemaVersion a
  // required literal for writes.
  schemaVersion: z.literal(SCENARIO_ASSUMPTIONS_SCHEMA_VERSION).optional(),
  meta: ScenarioAssumptionsMetaSchema.optional(),

  // Hiring assumptions
  newHires: z
    .array(
      z.object({
        date: z.string(),
        weeklyCapacity: z.number(),
        payRate: z.number(),
        billRate: z.number().optional(),
        name: z.string().optional(),
        // Role the hire fills. Metadata for labeling + UI rate defaulting (the
        // role's rate-card cascade seeds pay/bill in the editor); the engine
        // still computes from the explicit rates above. roleName is a display
        // snapshot so summaries render without a lookup.
        roleId: z.string().uuid().optional(),
        roleName: z.string().optional(),
        // Enhanced: include estimated employer cost multiplier
        employerCostMultiplier: z.number().optional(), // e.g., 1.25 for 25% overhead
        // Plan 05: cost a modeled hire by employment type. FTE (FULL_TIME /
        // PART_TIME) carries employer taxes/benefits; contractor types
        // (CONTRACTOR / FREELANCER) do not. Defaults to FULL_TIME when omitted.
        employmentType: z.nativeEnum(EmploymentType).optional(),
        // AG1 (Plan 09 T3.2): realism for modeled hires — all optional, absent ⇒
        // today's behavior (full output from the hire date).
        leadTimeWeeks: z.number().optional(), // delay before they start contributing
        rampMonths: z.number().optional(), // billable output ramps 0→100% over N months
        onboardingCost: z.number().optional(), // one-time cost in their first active period
        // AG4 (Plan 09 T3.3): paid time-off so a modeled hire isn't more optimistic
        // than a real member. Reduces billable output (not pay). UI defaults this to
        // the org-average PTO (D14).
        ptoWeeksPerYear: z.number().optional(),
        // Share of the hire's (PTO- and ramp-adjusted) billable hours actually
        // booked to client work, 0–100. Drives BOTH revenue and the hire's
        // utilization so the two reconcile. Absent ⇒ 100 (fully billable).
        billablePercent: z.number().optional(),
      }),
    )
    .optional(),

  // Rate change assumptions
  rateChanges: z
    .array(
      z.object({
        memberId: z.string().uuid(),
        effectiveDate: z.string(),
        // Optional: when omitted, the engine keeps the member's existing pay
        // rate (e.g. a bill-rate-only increase). Mirrors newBillRate.
        newPayRate: z.number().optional(),
        newBillRate: z.number().optional(),
      }),
    )
    .optional(),

  // Allocation assumptions
  allocationChanges: z
    .array(
      z.object({
        memberId: z.string().uuid(),
        projectId: z.string().uuid().optional(),
        entityId: z.string().uuid().optional(),
        roleId: z.string().uuid().optional(),
        // Display label for the client/project target (e.g. "Client · Acme"),
        // persisted so the summary renders the target without a name lookup.
        label: z.string().optional(),
        startDate: z.string(),
        endDate: z.string(),
        percent: z.number(),
      }),
    )
    .optional(),

  // Departure assumptions
  departures: z
    .array(
      z.object({
        memberId: z.string().uuid(),
        effectiveDate: z.string(),
      }),
    )
    .optional(),

  // Compensation/Benefit Changes - NEW
  compensationChanges: z
    .array(
      z.object({
        memberId: z.string().uuid(),
        effectiveDate: z.string(),
        changeType: z.enum(['SALARY_INCREASE', 'BONUS', 'PROMOTION']),
        percentChange: z.number().optional(), // e.g., 5 for 5% raise
        absoluteChange: z.number().optional(), // e.g., 5000 for $5k bonus
      }),
    )
    .optional(),

  // Benefit plan changes - NEW (affects all members)
  benefitChanges: z
    .array(
      z.object({
        effectiveDate: z.string(),
        changeType: z.enum(['ADD_BENEFIT', 'REMOVE_BENEFIT', 'MODIFY_BENEFIT']),
        benefitName: z.string(), // e.g., "Dental Insurance", "401k Match"
        employerCostPerMember: z.number().optional(), // Monthly cost per enrolled member
        employerCostPercent: z.number().optional(), // % of salary
        affectedMemberIds: z.array(z.string().uuid()).optional(), // Specific members, or all if omitted
      }),
    )
    .optional(),

  // Tax rate changes - NEW (e.g., SUTA rate adjustment)
  taxRateChanges: z
    .array(
      z.object({
        effectiveDate: z.string(),
        taxCode: z.string(), // e.g., "SUTA", "SIT"
        newRatePercent: z.number(), // e.g., 3.5 for 3.5%
        previousRatePercent: z.number().optional(), // For comparison
      }),
    )
    .optional(),

  // Global cost assumptions - NEW
  costAssumptions: z
    .object({
      // Override default employer tax rate (percentage)
      employerTaxOverridePercent: z.number().optional(),
      // Override default employer benefit rate (percentage)
      employerBenefitOverridePercent: z.number().optional(),
      // Annual inflation adjustment for salaries
      annualInflationPercent: z.number().optional(),
      // AG3 (Plan 09 T3.1): coarse non-labor operating overhead ($/month).
      // Distinct from employerCostMultiplier (a *labor* on-cost) — this is opex
      // (rent, software, G&A). Reduces profit so margin is a real P&L margin.
      monthlyOverhead: z.number().optional(),
      // AG2 (Plan 09 T3.4): annual bill-rate escalator (%), the revenue-side
      // counterpart to annualInflationPercent — compounds rates over the horizon.
      annualBillRateEscalatorPercent: z.number().optional(),
    })
    .optional(),

  // ---- Plan 05 — new axes ----------------------------------------------

  // Employment-type changes. Convert a member contractor↔FTE on a date; the
  // engine flips the cost structure from that date forward (employer
  // taxes/benefits apply for FTE, not for contractor). The baseline type is
  // read from member.employmentType.
  employmentTypeChanges: z
    .array(
      z.object({
        memberId: z.string().uuid(),
        effectiveDate: z.string(),
        newType: z.nativeEnum(EmploymentType),
      }),
    )
    .optional(),

  // Deal win/loss. "If we win deal X (probability p)" — stages the modeled
  // staffing (synthetic allocations) and adds probability-weighted revenue.
  // The single forecast applies the EXPECTED (p-weighted) contribution; the
  // portfolio view (results.portfolio) blends best/expected/worst (see the
  // engine's deal helper for the documented weighting). `probability` defaults
  // to the deal's own probability when omitted (resolved at calc time).
  dealChanges: z
    .array(
      z.object({
        dealId: z.string().uuid(),
        dealName: z.string().optional(),
        // 0–100. Omitted ⇒ default to deal.probability (crm.prisma) at calc time.
        // This is the HEADLINE-driving probability: the scenario service resolves
        // it from rep confidence or the derived win-rate per the tenant's T10
        // `headlineProbabilityDriver` setting before the engine runs.
        probability: z.number().optional(),
        // T10 — both raw probabilities carried through so the engine can compute
        // the NON-headline (alternate) expected contribution alongside, keeping
        // the gap visible. Set by the service at calc time; either may be null
        // (no rep confidence, or derived withheld at cold-start). Not client-set.
        repProbability: z.number().nullable().optional(),
        derivedProbability: z.number().nullable().optional(),
        // AG6 (Plan 09 T1.2): the deal's CRM client, resolved at calc time so
        // deal staffing attributes to that client's by-client pivot instead of
        // "Unassigned". A Deal has no project (crm.prisma) → client only.
        entityId: z.string().uuid().optional(),
        entityName: z.string().optional(),
        staffing: z
          .array(
            z.object({
              memberId: z.string().uuid().optional(),
              roleId: z.string().uuid().optional(),
              name: z.string().optional(),
              weeklyHours: z.number(),
              billRate: z.number(),
              payRate: z.number().optional(),
              employmentType: z.nativeEnum(EmploymentType).optional(),
              startDate: z.string(),
              endDate: z.string(),
            }),
          )
          .optional(),
      }),
    )
    .optional(),

  // Client/project churn. First-class "lose client Y / project ends on date"
  // lever that removes that entity's/project's allocations + revenue from the
  // forecast from the effective date forward — distinct from a member
  // departure (which is the wrong axis). Provide entityId (whole client) or
  // projectId (single project).
  clientChurn: z
    .array(
      z.object({
        entityId: z.string().uuid().optional(),
        projectId: z.string().uuid().optional(),
        label: z.string().optional(),
        effectiveDate: z.string(),
      }),
    )
    .optional(),

  // T10 — which probability drove the headline deal-revenue weighting for this
  // calc. Resolved from the tenant's `deal.scoring` preference by the scenario
  // service and stamped here so the engine can label the portfolio meta and
  // weight the alternate (non-headline) contribution. Defaults to `rep`.
  headlineProbabilityDriver: z.enum(['derived', 'rep']).optional(),
})

export type ScenarioAssumptions = z.infer<typeof ScenarioAssumptionsSchema>

/**
 * Canonical, strict write-time validator. Requires `schemaVersion: 1` inside the
 * document and folds the former hand-rolled `validateAssumptions` numeric/range
 * checks (scenario.service.ts) into Zod so there is a single validator shared by
 * the API and web. Unknown keys are stripped on parse.
 */
export const ScenarioAssumptionsV1Schema = ScenarioAssumptionsSchema.extend({
  schemaVersion: z.literal(SCENARIO_ASSUMPTIONS_SCHEMA_VERSION),
}).superRefine((value, ctx) => {
  // Custom messages are authored to read AFTER a humanized field label that
  // `formatScenarioAssumptionIssues` prepends (e.g. "Weekly hours " + message).
  value.newHires?.forEach((hire, index) => {
    if (!(hire.weeklyCapacity > 0 && hire.weeklyCapacity <= 168)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['newHires', index, 'weeklyCapacity'],
        message: 'must be greater than 0 and at most 168',
      })
    }
    if (hire.payRate < 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['newHires', index, 'payRate'],
        message: 'cannot be negative',
      })
    }
    if (
      hire.billablePercent != null &&
      (hire.billablePercent < 0 || hire.billablePercent > 100)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['newHires', index, 'billablePercent'],
        message: 'must be between 0 and 100',
      })
    }
  })

  value.rateChanges?.forEach((change, index) => {
    if (change.newPayRate != null && change.newPayRate < 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['rateChanges', index, 'newPayRate'],
        message: 'cannot be negative',
      })
    }
  })

  value.allocationChanges?.forEach((change, index) => {
    if (change.percent < 0 || change.percent > 200) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['allocationChanges', index, 'percent'],
        message: 'must be between 0 and 200',
      })
    }
  })

  // Plan 05: deal win probability is a percentage; staffing rows need positive
  // hours and non-negative rates.
  value.dealChanges?.forEach((deal, index) => {
    if (
      deal.probability != null &&
      (deal.probability < 0 || deal.probability > 100)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['dealChanges', index, 'probability'],
        message: 'must be between 0 and 100',
      })
    }
    deal.staffing?.forEach((row, rowIndex) => {
      if (!(row.weeklyHours > 0 && row.weeklyHours <= 168)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['dealChanges', index, 'staffing', rowIndex, 'weeklyHours'],
          message: 'must be greater than 0 and at most 168',
        })
      }
      if (row.billRate < 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['dealChanges', index, 'staffing', rowIndex, 'billRate'],
          message: 'cannot be negative',
        })
      }
    })
  })

  // Plan 05: a churn row must target either a client (entityId) or a project.
  value.clientChurn?.forEach((churn, index) => {
    if (!churn.entityId && !churn.projectId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['clientChurn', index, 'entityId'],
        message: 'requires a client or project',
      })
    }
  })
})

// ---------------------------------------------------------------------------
// Friendly error formatting (plan 03). The Zod validator reports machine paths
// like `rateChanges.0.newPayRate`; this maps them to end-user language such as
// "Rate change #1 — New pay rate is required" for the toast shown on save.
// ---------------------------------------------------------------------------

/** Singular, human label per assumption section. */
const SCENARIO_SECTION_LABELS: Record<string, string> = {
  newHires: 'New hire',
  rateChanges: 'Rate change',
  allocationChanges: 'Allocation change',
  departures: 'Departure',
  compensationChanges: 'Compensation change',
  benefitChanges: 'Benefit change',
  taxRateChanges: 'Tax rate change',
  costAssumptions: 'Cost assumption',
  employmentTypeChanges: 'Employment-type change',
  dealChanges: 'Deal',
  clientChurn: 'Client churn',
}

/** Human label per assumption field. */
const SCENARIO_FIELD_LABELS: Record<string, string> = {
  date: 'start date',
  startDate: 'start date',
  endDate: 'end date',
  effectiveDate: 'effective date',
  weeklyCapacity: 'weekly hours',
  payRate: 'pay rate',
  billRate: 'bill rate',
  billablePercent: 'billable percent',
  newPayRate: 'new pay rate',
  newBillRate: 'new bill rate',
  percent: 'allocation percent',
  memberId: 'team member',
  name: 'name',
  changeType: 'change type',
  percentChange: 'percent change',
  absoluteChange: 'amount',
  benefitName: 'benefit name',
  employerCostPerMember: 'employer cost per member',
  employerCostPercent: 'employer cost percent',
  affectedMemberIds: 'affected team members',
  taxCode: 'tax code',
  newRatePercent: 'new rate percent',
  previousRatePercent: 'previous rate percent',
  employerTaxOverridePercent: 'employer tax override',
  employerBenefitOverridePercent: 'employer benefit override',
  annualInflationPercent: 'annual inflation',
  projectId: 'project',
  entityId: 'entity',
  roleId: 'role',
  employerCostMultiplier: 'employer cost multiplier',
  weeklyCapacityHours: 'weekly hours',
  newType: 'new employment type',
  dealId: 'deal',
  dealName: 'deal name',
  probability: 'probability',
  weeklyHours: 'weekly hours',
}

function humanizeKey(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase()
}

function capitalizeFirst(text: string): string {
  return text.length ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : text
}

function friendlyIssueReason(issue: z.ZodIssue): string {
  if (issue.code === z.ZodIssueCode.invalid_type) {
    const typeIssue = issue as z.ZodInvalidTypeIssue
    if (typeIssue.received === 'undefined') return 'is required'
    if (typeIssue.expected === 'number') return 'must be a number'
    if (typeIssue.expected === 'string') return 'must be text'
    return `must be ${typeIssue.expected}`
  }
  if (issue.code === z.ZodIssueCode.invalid_enum_value) {
    return 'is not a valid option'
  }
  // Custom (superRefine) and other messages already read naturally after a label.
  return issue.message
}

/**
 * Turn a ZodError's issues into one human-readable sentence for the save toast.
 * Deduplicates identical lines and never leaks raw field paths.
 */
export function formatScenarioAssumptionIssues(issues: z.ZodIssue[]): string {
  const seen = new Set<string>()
  const lines: string[] = []

  for (const issue of issues) {
    const [sectionKey, second, ...rest] = issue.path
    const section =
      typeof sectionKey === 'string'
        ? SCENARIO_SECTION_LABELS[sectionKey]
        : undefined

    let label: string
    let fieldKey: string | undefined

    if (section) {
      if (typeof second === 'number') {
        // Array section: "Rate change #1" + the deepest field in the path.
        const deepest = rest.length ? rest[rest.length - 1] : undefined
        fieldKey = typeof deepest === 'string' ? deepest : undefined
        label = `${section} #${second + 1}`
      } else {
        // Object section (costAssumptions): the field is the second segment.
        fieldKey = typeof second === 'string' ? second : undefined
        label = section
      }
    } else {
      label =
        typeof sectionKey === 'string'
          ? capitalizeFirst(humanizeKey(sectionKey))
          : 'Assumptions'
    }

    const fieldLabel = fieldKey
      ? (SCENARIO_FIELD_LABELS[fieldKey] ?? humanizeKey(fieldKey))
      : undefined
    const reason = friendlyIssueReason(issue)
    const line = fieldLabel
      ? `${label} — ${capitalizeFirst(fieldLabel)} ${reason}`
      : `${label} ${reason}`

    if (!seen.has(line)) {
      seen.add(line)
      lines.push(line)
    }
  }

  return lines.join('; ')
}

/** A normalized, version-stamped assumptions document (the engine/UI always see this). */
export type ScenarioAssumptionsV1 = ScenarioAssumptions & {
  schemaVersion: typeof SCENARIO_ASSUMPTIONS_SCHEMA_VERSION
}

/**
 * Pure back-compat read path. Maps any stored assumptions blob → V1:
 * - already-V1 documents (`schemaVersion === 1`) are returned unchanged, so the
 *   function is idempotent (`normalize(normalize(x)) === normalize(x)` by value);
 * - legacy/version-less blobs (every existing row) carry every known lever and
 *   the typed `meta` (incl. the old review flag) through verbatim — lossless —
 *   and get `schemaVersion: 1` stamped.
 *
 * Tolerant by design: it never throws on out-of-range legacy values so existing
 * scenarios always load. Strict validation happens on WRITE via
 * `ScenarioAssumptionsV1Schema`.
 */
export function normalizeAssumptions(raw: unknown): ScenarioAssumptionsV1 {
  const obj =
    raw && typeof raw === 'object' && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : {}

  // Already V1 — return as-is (idempotency).
  if (obj.schemaVersion === SCENARIO_ASSUMPTIONS_SCHEMA_VERSION) {
    return obj as ScenarioAssumptionsV1
  }

  // Legacy (v0): the V1 shape is a superset of v0, so the mapping is a lossless
  // carry-through plus a version stamp. `meta` (the former side-channel) is
  // already in its now-typed location and is preserved here.
  return {
    ...obj,
    schemaVersion: SCENARIO_ASSUMPTIONS_SCHEMA_VERSION,
  } as ScenarioAssumptionsV1
}

// ---------------------------------------------------------------------------
// Scenario lever-wiredness registry (Scenarios overhaul, plan 02)
//
// Single source of truth for whether each assumption type the editor offers is
// actually consumed by the forecast engine
// (apps/api/src/member-resources/services/resource-forecast.service.ts).
//
// Two levers (rate / allocation changes) once shipped collected-but-ignored and
// nothing surfaced it. Plan 01 wired every current lever; this map keeps that
// honest going forward: any type marked PARTIAL or UNWIRED makes the editor
// render a visible "not applied to results yet" notice, so a future lever (plan
// 05) added UI-first can never masquerade as working.
//
// Statuses are DERIVED from the post-plan-01 engine — update this map only when
// the engine's behavior for a type actually changes.
// ---------------------------------------------------------------------------

// The lever (assumption) types only — excludes the document-level metadata keys
// (`schemaVersion`, `meta` from plan 03; `headlineProbabilityDriver` is the T10
// config field, resolved by the service, not an editable lever).
export type ScenarioAssumptionType = Exclude<
  keyof ScenarioAssumptions,
  'schemaVersion' | 'meta' | 'headlineProbabilityDriver'
>

export type LeverWiredness = 'WIRED' | 'PARTIAL' | 'UNWIRED'

export interface LeverWirednessEntry {
  /** Engine support for this assumption type. */
  status: LeverWiredness
  /** Editor section label (kept in sync with ScenarioEditor section titles). */
  label: string
  /**
   * Required for PARTIAL: which part of the lever is not yet applied. Shown in
   * the editor notice so users know exactly what is/ isn't reflected.
   */
  note?: string
}

/**
 * Engine-wiredness of every scenario assumption type, cross-checked against the
 * post-plan-01 engine. Engine references (resource-forecast.service.ts):
 * - newHires:          applied to capacity + revenue
 * - rateChanges:       buildRateChangeMap + resolveRateChange (rev/cost)
 * - allocationChanges: allocationChangeHoursDelta (capacity + revenue)
 * - departures:        buildDepartureMap + departureClip (capacity + revenue)
 * - compensationChanges: SALARY_INCREASE / PROMOTION / BONUS all handled
 * - benefitChanges:    employer-cost adjustments applied per period
 * - taxRateChanges:    employer-tax adjustments applied per period
 * - costAssumptions:   employer tax/benefit overrides + annualInflationPercent
 * - employmentTypeChanges: contractor↔FTE conversion flips employer cost (Plan 05)
 * - dealChanges:       probability-weighted staffing + revenue (Plan 05)
 * - clientChurn:       removes a client/project's allocations + revenue (Plan 05)
 *
 * Every current lever is WIRED, so the editor shows no notices. Plan 05 levers
 * are registered WIRED at the moment their engine support landed (never UI-first).
 */
export const SCENARIO_LEVER_WIREDNESS: Record<
  ScenarioAssumptionType,
  LeverWirednessEntry
> = {
  newHires: { status: 'WIRED', label: 'New hires' },
  departures: { status: 'WIRED', label: 'Departures' },
  rateChanges: { status: 'WIRED', label: 'Rate changes' },
  allocationChanges: { status: 'WIRED', label: 'Allocation changes' },
  compensationChanges: { status: 'WIRED', label: 'Compensation changes' },
  benefitChanges: { status: 'WIRED', label: 'Benefit plan changes' },
  taxRateChanges: { status: 'WIRED', label: 'Tax rate changes' },
  costAssumptions: { status: 'WIRED', label: 'Global cost assumptions' },
  employmentTypeChanges: { status: 'WIRED', label: 'Employment type changes' },
  dealChanges: { status: 'WIRED', label: 'Deals (win / loss)' },
  clientChurn: { status: 'WIRED', label: 'Client / project churn' },
}

/**
 * Pure helper backing the editor guardrail. Returns the notice to render for a
 * given wiredness entry, or null when the lever is fully WIRED (no notice).
 * Kept pure so it can be unit-tested with a synthetic UNWIRED/PARTIAL entry
 * without mocking the registry.
 */
export function getLeverWirednessNotice(
  entry: LeverWirednessEntry | undefined,
): { message: string } | null {
  if (!entry || entry.status === 'WIRED') return null
  if (entry.status === 'PARTIAL') {
    return {
      message: entry.note
        ? `This assumption is only partially applied to results — ${entry.note}`
        : 'This assumption is only partially applied to results.',
    }
  }
  return { message: "This assumption isn't applied to results yet." }
}

/**
 * Count of authored rows per assumption type — used for privacy-safe telemetry
 * (counts only, never pay rates / member ids / comp values). `costAssumptions`
 * is an object, not an array, so it counts as 1 when any field is set.
 */
export function countScenarioAssumptionsByType(
  assumptions: ScenarioAssumptions | null | undefined,
): Record<ScenarioAssumptionType, number> {
  const a = assumptions ?? {}
  const cost = a.costAssumptions
  const hasCost =
    !!cost &&
    Object.values(cost).some(value => value !== undefined && value !== null)
  return {
    newHires: a.newHires?.length ?? 0,
    departures: a.departures?.length ?? 0,
    rateChanges: a.rateChanges?.length ?? 0,
    allocationChanges: a.allocationChanges?.length ?? 0,
    compensationChanges: a.compensationChanges?.length ?? 0,
    benefitChanges: a.benefitChanges?.length ?? 0,
    taxRateChanges: a.taxRateChanges?.length ?? 0,
    costAssumptions: hasCost ? 1 : 0,
    employmentTypeChanges: a.employmentTypeChanges?.length ?? 0,
    dealChanges: a.dealChanges?.length ?? 0,
    clientChurn: a.clientChurn?.length ?? 0,
  }
}

// ---------------------------------------------------------------------------
// Plan 05 — result breakdowns and the deal portfolio. Both are derived from the
// per-period engine output and embedded in the results blob (no migration), so
// the workspace can pivot results by client/project/deal/member and show a
// best/expected/worst portfolio. Baseline results carry the same shapes, which
// is what lets the UI render a Δ-vs-baseline per breakdown row.
// ---------------------------------------------------------------------------

/** One pivot row: an entity (client/project/deal/member) with its horizon totals. */
export const ScenarioBreakdownRowSchema = z.object({
  /** Stable group id (entity/project/deal/member id) or 'unassigned'. */
  key: z.string(),
  label: z.string(),
  revenue: z.number(),
  cost: z.number(),
  hours: z.number(),
})

export type ScenarioBreakdownRow = z.infer<typeof ScenarioBreakdownRowSchema>

/** Results pivoted by each supported dimension. Each list sums to the org total. */
export const ScenarioBreakdownsSchema = z.object({
  byClient: z.array(ScenarioBreakdownRowSchema),
  byProject: z.array(ScenarioBreakdownRowSchema),
  byDeal: z.array(ScenarioBreakdownRowSchema),
  byMember: z.array(ScenarioBreakdownRowSchema),
})

export type ScenarioBreakdowns = z.infer<typeof ScenarioBreakdownsSchema>

export const ScenarioBreakdownDimensions = [
  'byClient',
  'byProject',
  'byDeal',
  'byMember',
] as const
export type ScenarioBreakdownDimension =
  (typeof ScenarioBreakdownDimensions)[number]

export const ScenarioPortfolioPointSchema = z.object({
  totalProjectedRevenue: z.number(),
  totalProjectedCost: z.number(),
  totalProjectedProfit: z.number(),
})

export type ScenarioPortfolioPoint = z.infer<
  typeof ScenarioPortfolioPointSchema
>

/**
 * Best / expected / worst roll-up of deal win/loss outcomes (Plan 05).
 *
 * Probability-weighting math (explicit & documented so the number is trusted):
 * - Let B = the forecast totals from everything EXCEPT deal wins (existing
 *   allocations + all other levers). This is the WORST case — no modeled deal
 *   is won.
 * - Each deal i has a modeled full value Vᵢ (revenue/cost computed from its
 *   staffing rows over the horizon) and a win probability pᵢ ∈ [0,1].
 * - EXPECTED = B + Σ pᵢ·Vᵢ  ← what the single scenario forecast shows.
 * - BEST     = B + Σ Vᵢ      ← every modeled deal won.
 * - WORST    = B             ← no modeled deal won.
 * Derived analytically from per-period dealPortfolioMeta, so no extra engine
 * passes are needed and best ≥ expected ≥ worst always holds (pᵢ ∈ [0,1]).
 */
export const ScenarioPortfolioSchema = z.object({
  worst: ScenarioPortfolioPointSchema,
  expected: ScenarioPortfolioPointSchema,
  best: ScenarioPortfolioPointSchema,
  // T10 — the expected case re-weighted by the NON-headline probability source
  // (the rep number when the headline is derived, and vice versa). Surfaced so
  // the workspace can show both numbers; the gap between `expected` and
  // `alternate` is the Lever-2 divergence. Present only when deals are modeled.
  alternate: ScenarioPortfolioPointSchema.optional(),
  // Which source drove the headline `expected` case.
  headlineProbabilityDriver: z.enum(['derived', 'rep']).optional(),
})

export type ScenarioPortfolio = z.infer<typeof ScenarioPortfolioSchema>

// ---------------------------------------------------------------------------
// Plan 05 — pure aggregators (used by the scenario service; unit-tested
// directly). They roll the engine's per-period `breakdownRows` /
// `dealPortfolioMeta` up into the persisted `breakdowns` / `portfolio` shapes.
// ---------------------------------------------------------------------------

const UNASSIGNED_KEY = 'unassigned'

function accumulateBreakdown(
  acc: Map<string, ScenarioBreakdownRow>,
  key: string,
  label: string,
  row: RevenueBreakdownRow,
): void {
  const existing = acc.get(key)
  if (existing) {
    existing.revenue += row.revenue
    existing.cost += row.cost
    existing.hours += row.hours
  } else {
    acc.set(key, {
      key,
      label,
      revenue: row.revenue,
      cost: row.cost,
      hours: row.hours,
    })
  }
}

function sortBreakdown(
  acc: Map<string, ScenarioBreakdownRow>,
): ScenarioBreakdownRow[] {
  return Array.from(acc.values()).sort((a, b) => b.revenue - a.revenue)
}

/**
 * Aggregate per-period breakdown rows into the four pivots. Every row is counted
 * exactly once per dimension (multi-target members appear once per allocation,
 * not duplicated), so each pivot's revenue sums to the org total — the
 * reconciliation invariant the engine spec asserts. Rows with no value for a
 * dimension fall into a single "Unassigned"/"No deal" bucket.
 */
export function aggregateScenarioBreakdowns(
  revenueForecast: RevenueForecastPoint[],
): ScenarioBreakdowns {
  const byClient = new Map<string, ScenarioBreakdownRow>()
  const byProject = new Map<string, ScenarioBreakdownRow>()
  const byDeal = new Map<string, ScenarioBreakdownRow>()
  const byMember = new Map<string, ScenarioBreakdownRow>()

  for (const point of revenueForecast) {
    for (const row of point.breakdownRows ?? []) {
      accumulateBreakdown(
        byClient,
        row.entityId ?? UNASSIGNED_KEY,
        row.entityName ?? 'Unassigned',
        row,
      )
      accumulateBreakdown(
        byProject,
        row.projectId ?? UNASSIGNED_KEY,
        row.projectName ?? 'Unassigned',
        row,
      )
      accumulateBreakdown(
        byDeal,
        row.dealId ?? UNASSIGNED_KEY,
        row.dealName ?? 'No deal',
        row,
      )
      accumulateBreakdown(
        byMember,
        row.memberId ?? UNASSIGNED_KEY,
        row.memberName ?? 'Unassigned',
        row,
      )
    }
  }

  return {
    byClient: sortBreakdown(byClient),
    byProject: sortBreakdown(byProject),
    byDeal: sortBreakdown(byDeal),
    byMember: sortBreakdown(byMember),
  }
}

/**
 * Derive the best/expected/worst portfolio from per-period deal accounting and
 * the (expected) forecast totals. See ScenarioPortfolioSchema for the weighting
 * math. Returns undefined when the scenario has no deal contributions.
 */
export function aggregateScenarioPortfolio(
  revenueForecast: RevenueForecastPoint[],
  totalRevenue: number,
  totalCost: number,
): ScenarioPortfolio | undefined {
  let expectedRevenue = 0
  let fullRevenue = 0
  let expectedCost = 0
  let fullCost = 0
  // T10 — running alternate (non-headline) expected deal contribution.
  let alternateRevenue = 0
  let alternateCost = 0
  let hasAlternate = false
  let headlineProbabilityDriver: 'derived' | 'rep' | undefined
  let hasDeals = false
  for (const point of revenueForecast) {
    const meta = point.dealPortfolioMeta
    if (!meta) continue
    hasDeals = true
    expectedRevenue += meta.expectedRevenue
    fullRevenue += meta.fullRevenue
    expectedCost += meta.expectedCost
    fullCost += meta.fullCost
    if (meta.alternateExpectedRevenue != null) {
      hasAlternate = true
      alternateRevenue += meta.alternateExpectedRevenue
      alternateCost += meta.alternateExpectedCost ?? 0
    }
    headlineProbabilityDriver ??= meta.headlineProbabilityDriver
  }
  if (!hasDeals) return undefined

  // Worst = totals with the expected deal contribution removed (no deal won).
  const worstRevenue = totalRevenue - expectedRevenue
  const worstCost = totalCost - expectedCost
  const bestRevenue = worstRevenue + fullRevenue
  const bestCost = worstCost + fullCost

  const point = (rev: number, cost: number): ScenarioPortfolioPoint => ({
    totalProjectedRevenue: rev,
    totalProjectedCost: cost,
    totalProjectedProfit: rev - cost,
  })

  // T10 — the alternate case swaps the deal contribution for the non-headline
  // weighting on the same worst-case base (everything except deal wins).
  const alternate = hasAlternate
    ? point(worstRevenue + alternateRevenue, worstCost + alternateCost)
    : undefined

  return {
    worst: point(worstRevenue, worstCost),
    expected: point(totalRevenue, totalCost),
    best: point(bestRevenue, bestCost),
    ...(alternate ? { alternate } : {}),
    ...(headlineProbabilityDriver ? { headlineProbabilityDriver } : {}),
  }
}

/**
 * Plan 05: strip the bulky per-period attribution fields from a revenue forecast
 * before it is persisted in the results JSONB — the aggregates
 * (breakdowns/portfolio) carry the information the workspace needs.
 */
export function stripPerPeriodBreakdownData(
  revenueForecast: RevenueForecastPoint[],
): RevenueForecastPoint[] {
  return revenueForecast.map(point => {
    if (
      point.breakdownRows === undefined &&
      point.dealPortfolioMeta === undefined
    ) {
      return point
    }
    const { breakdownRows: _b, dealPortfolioMeta: _d, ...rest } = point
    return rest
  })
}

// Scenario Results
export const ScenarioResultsSchema = z.object({
  currency: z.string().optional(),
  capacityForecast: z.array(CapacityForecastPointSchema),
  revenueForecast: z.array(RevenueForecastPointSchema),
  utilizationForecast: z.array(UtilizationForecastPointSchema),
  summary: z.object({
    averageCapacity: z.number(),
    averageUtilization: z.number(),
    // A1 (Plan 09 T1.1): mean of the best-case (all live deals won at full
    // staffing) utilization across periods. Present only when the scenario has
    // deal-change assumptions; the expected `averageUtilization` is untouched.
    averageBestCaseUtilization: z.number().optional(),
    totalProjectedRevenue: z.number(),
    totalProjectedCost: z.number(),
    totalProjectedProfit: z.number(),
    profitMargin: z.number(),
    // AG3 + AG1 (Plan 09 T3): total non-labor overhead + onboarding netted into
    // totalProjectedProfit/profitMargin. Present (and > 0) only when those levers
    // are used; the labor-only totalProjectedCost is unchanged.
    totalOtherCosts: z.number().optional(),
    // Enhanced cost breakdown
    costBreakdown: z
      .object({
        totalGrossPayroll: z.number(),
        totalEmployerTaxes: z.number(),
        totalEmployerBenefits: z.number(),
        totalEmployerCost: z.number(),
        // True profit after all employer costs
        trueProfit: z.number(),
        trueProfitMargin: z.number(),
        // B4 (Plan 09 T2.1): comp-loaded basis (see per-period costBreakdown).
        // `totalGrossPayroll`/`totalEmployerCost` above stay rate-card so the
        // headline and member table reconcile; these drive the finance Full Cost
        // Breakdown card so it stops labelling rate-card numbers "true cost".
        trueGrossPayroll: z.number().optional(),
        trueEmployerCost: z.number().optional(),
      })
      .optional(),
  }),
  // Plan 05: results pivoted by client/project/deal/member (each sums to the
  // org total). Present once a scenario is calculated post-Plan-05.
  breakdowns: ScenarioBreakdownsSchema.optional(),
  // Plan 05: best/expected/worst roll-up of deal win/loss outcomes. Present
  // when the scenario has deal-change assumptions.
  portfolio: ScenarioPortfolioSchema.optional(),
})

export type ScenarioResults = z.infer<typeof ScenarioResultsSchema>

// ---------------------------------------------------------------------------
// Scenario results — typed/versioned (Scenarios overhaul, plan 03)
//
// Adds the version stamp and the two slots plan 04 (baseline + live workspace)
// needs: `calculatedAt` (stale-detection vs ForecastScenario.updatedAt — PRD
// §5.5) and an embedded `baseline` forecast (the no-assumptions run, populated
// by plan 04 — left unset here). Legacy results read as-is and take this shape
// on the next recalculation.
// ---------------------------------------------------------------------------

/** Server-written provenance for re-authorizing stored forecast results. */
export const ForecastSourceAccessSchema = z.object({
  version: z.literal(1),
  organizationId: z.string().uuid(),
  sources: z.array(
    z.object({
      moduleType: z.nativeEnum(ModuleTypeEnum),
      rows: z.array(
        z.object({
          id: z.string().uuid(),
          projectId: z.string().nullable().optional(),
          moduleType: z.string().nullable().optional(),
          moduleId: z.string().nullable().optional(),
        }),
      ),
      fields: z.array(z.string()),
    }),
  ),
})
export type ForecastSourceAccess = z.infer<typeof ForecastSourceAccessSchema>

export const ScenarioResultsV1Schema = ScenarioResultsSchema.extend({
  sourceAccess: ForecastSourceAccessSchema.optional(),
  schemaVersion: z.literal(SCENARIO_ASSUMPTIONS_SCHEMA_VERSION).optional(),
  /** ISO timestamp of the calculation that produced these results. */
  calculatedAt: z.string().optional(),
  /**
   * No-assumptions baseline forecast, stored next to the scenario forecast for
   * in-scenario deltas (PRD §5.5). Plan 04 populates this; unset until then.
   */
  baseline: ScenarioResultsSchema.optional(),
})

export type ScenarioResultsV1 = z.infer<typeof ScenarioResultsV1Schema>

/**
 * Back-compat read path for results. Legacy results lack `schemaVersion`; they
 * are structurally valid `ScenarioResults`, so we return them unchanged. They
 * acquire the V1 shape (schemaVersion + calculatedAt) on the next recalc.
 * Idempotent and lossless.
 */
export function normalizeResults(
  raw: unknown,
): ScenarioResultsV1 | null | undefined {
  if (raw === null) return null
  if (raw === undefined) return undefined
  return raw as ScenarioResultsV1
}

// Forecast Snapshot
export const ForecastSnapshotSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  snapshotDate: z.date(),
  forecastType: z.nativeEnum(ForecastType),
  periodStart: z.date(),
  periodEnd: z.date(),
  data: z.any(),
  createdAt: z.date(),
})

export type ForecastSnapshot = z.infer<typeof ForecastSnapshotSchema>

export const CreateForecastSnapshotSchema = ForecastSnapshotSchema.omit({
  id: true,
  createdAt: true,
})

export type CreateForecastSnapshot = z.infer<
  typeof CreateForecastSnapshotSchema
>

// Forecast Scenario
export const ForecastScenarioSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  name: z.string(),
  description: z.string().optional().nullable(),
  baselineDate: z.date(),
  assumptions: ScenarioAssumptionsSchema,
  results: ScenarioResultsV1Schema.optional().nullable(),
  status: z.nativeEnum(ScenarioStatus).default(ScenarioStatus.DRAFT),
  createdBy: z.string().uuid(),
  createdAt: z.date(),
  updatedAt: z.date(),
  creator: z.any().optional(),
})

export type ForecastScenario = z.infer<typeof ForecastScenarioSchema>

export const CreateForecastScenarioSchema = ForecastScenarioSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  creator: true,
})

export type CreateForecastScenario = z.infer<
  typeof CreateForecastScenarioSchema
>

export const UpdateForecastScenarioSchema =
  CreateForecastScenarioSchema.partial().omit({
    organizationId: true,
    createdBy: true,
  })

export type UpdateForecastScenario = z.infer<
  typeof UpdateForecastScenarioSchema
>

// Forecast Timeline (combined view)
export const ForecastTimelineSchema = z.object({
  capacity: z.array(CapacityForecastPointSchema),
  revenue: z.array(RevenueForecastPointSchema),
  utilization: z.array(UtilizationForecastPointSchema),
  meta: ForecastTimelineMetaSchema.optional(),
})

export type ForecastTimeline = z.infer<typeof ForecastTimelineSchema>
